/**
 * The mobility DOMAIN CONTRACT — every DTO (snake_case, mirroring the backend
 * routes) + one typed api function per endpoint. This is the SINGLE surface the
 * four portals code against: a page imports `{ mobilityApi, type JobSummary }`
 * from here and never touches `lib/api.ts` prefixes or raw paths directly.
 *
 * Source of truth = the tmext_mobility routes (packages/mobility, base
 * `/mobility/api/v1`). DTOs are snake_case; pagination rides `meta.pagination`
 * (camelCase, M-12) via the `getPage`/`postPage` client variants. GET/POST only.
 *
 * Auth (OTP send/verify, /auth/me, logout) lives in `lib/mobility-auth.ts`; the
 * shared {@link Role} type is defined here and re-exported there.
 */

import { mobilityClient, type PagedResponse, type RequestOptions } from './api'

export type { ListSummaryMeta, PaginationMeta, PagedResponse, RequestOptions } from './api'
export { ApiError } from './api'

// ── Shared enums ────────────────────────────────────────────────────────────────

/** The four mobility roles (API_SPEC §13 access matrix). A principal may hold several. */
export type Role = 'EMPLOYEE' | 'HR_PARTNER' | 'HR_ADMIN'
export const ROLES: readonly Role[] = ['EMPLOYEE', 'HR_PARTNER', 'HR_ADMIN']

export type EntityType = 'BANK' | 'SUBSIDIARY'
export type FlowType = 'FLOW_1' | 'FLOW_2'
/**
 * WAVE M6 added `ON_HOLD`:
 *
 *   DRAFT ──publish──▶ OPEN ──hold──▶ ON_HOLD ──reactivate──▶ OPEN
 *                       │                 └────close────▶ CLOSED
 *                       └───close───▶ CLOSED ──reactivate──▶ OPEN
 *
 * HOLD IS A PAUSE; CLOSE IS AN ENDING. A hold stops the posting taking NEW
 * applications and does nothing else: everyone already in the pipeline stays,
 * and recruiters keep reviewing, progressing and offering throughout. A close
 * REJECTS the active pipeline, irreversibly (a terminal application status has
 * no exits and the status history is append-only), which is why only its
 * confirm dialog carries the danger warning.
 *
 * ON_HOLD → CLOSED is reachable directly, and must be: the alternative would be
 * reactivate-then-close, which republishes the job to every eligible employee
 * and accepts fresh applications purely in order to shut it down.
 *
 * NOT RETROACTIVE: postings held BEFORE this rule changed had their pipelines
 * rejected at hold time, and nothing restores those candidates.
 */
export type RequisitionStatus = 'DRAFT' | 'OPEN' | 'ON_HOLD' | 'CLOSED'

/**
 * WAVE M7 — one HR contact on an ENTITY. Deliberately the same shape as a
 * requisition's `subsidiary_hr_contacts` entry, because the entity list exists
 * to pre-fill that one and a conversion between two spellings of the same thing
 * is a bug waiting to happen.
 */
export interface HrContactDTO {
  email: string
  name?: string | null
}

/** WAVE M7 — what `GET /entities/suggest-codes` answers. */
export interface SuggestedEntityCodes {
  entity_code: string
  short_code: string
}
export type GradeDirection = 'BANK_TO_SUB' | 'SUB_TO_BANK' | 'BOTH'
export type StatusType = 'INITIAL' | 'INTERMEDIATE' | 'TERMINAL'
export type EligibilityBasis = 'SAME_GRADE' | 'PLUS_ONE'
export type RosterStatus = 'ACTIVE' | 'INACTIVE' | 'EXITED'
export type BatchStatus = 'processing' | 'completed' | 'failed'
export type SettingValueType = 'STRING' | 'NUMBER' | 'BOOLEAN' | 'JSON' | 'SECRET'

/** FLOW2 F-5: a job/application "channel" — derived from the POSTING entity's type. */
export type JobChannel = 'BANK' | 'SUBSIDIARY'
export type EligibilityStatus = 'ELIGIBLE' | 'INELIGIBLE' | 'UNAVAILABLE'
/** FLOW2 F-7: catalog-driven applications grouping (ACTIVE = non-TERMINAL statuses). */
export type ApplicationStatusGroup = 'ACTIVE' | 'CLOSED'
/** FLOW2 F-4: posting origin — MANUAL today (string-typed for forward compat). */
export type RequisitionSourceType = 'MANUAL' | (string & {})
/** Wave GM-M (S2): the applications-export render format (default `pdf`). */
export type ExportApplicationsFormat = 'pdf' | 'xlsx'

/** A single bulk-upload row failure (grades / eligibility / roster-sync reports). */
export interface RowFailure {
  row: number
  error: string
}

// ── DTOs (snake_case, per the route response schemas) ────────────────────────────

export interface Entity {
  id: string
  entity_code: string
  /**
   * Wave M3 — the SHORT company code that PREFIXES this entity's job codes
   * (`AXMF` → `AXMF435266`). Authored master data (uppercase alphanumeric,
   * 2-8 chars, unique); `null` until an HR_ADMIN sets it, and a
   * requisition cannot be posted by the entity until then.
   */
  short_code: string | null
  entity_name: string
  entity_type: EntityType
  domain: string | null
  hr_spoc_email: string | null
  /**
   * WAVE M7 — the SECONDARY HR contacts (the primary is `hr_spoc_email`).
   * Always an ARRAY, never null. Pre-fills a requisition’s subsidiary HR.
   */
  hr_contacts: HrContactDTO[]
  /** FLOW2 F-2 branding — absolute http(s) URL of the entity logo. */
  logo_url: string | null
  /** FLOW2 F-2 branding — the entity's public website. */
  website_url: string | null
  /** FLOW2 F-2 branding — free-text "About the company" copy. */
  about: string | null
  is_active: boolean
  metadata: unknown | null
  created_by: string
  created_at: string
  updated_at: string
}

/**
 * The FLOW2 F-2 SLIM entity projection `GET /entities` serves to EMPLOYEE
 * readers: identity + branding only — `domain`/`hr_spoc_email`/`metadata`/
 * `created_by`/timestamps are OPS fields and stay staff-only. The projection is
 * chosen SERVER-SIDE from the caller's role; use {@link mobilityApi.entities.listSlim}
 * on employee surfaces.
 */
export interface SlimEntity {
  id: string
  entity_code: string
  entity_name: string
  entity_type: EntityType
  logo_url: string | null
  website_url: string | null
  about: string | null
  is_active: boolean
}

/** A `GET /entities` row — the full DTO (staff) or the slim projection (employee). */
export type EntityListItem = Entity | SlimEntity

/**
 * One grade mapping — after Wave GM-S (GRADE_SIMPLIFICATION_PLAN §3) a row says
 * exactly ONE thing: "in this company, grade X is equivalent to bank grade Y".
 * `band`, `group_level` and the `effective_from`/`effective_to` window left the
 * wire with the columns; seniority ordering now lives in the `grade_ladder`
 * setting and `is_active` is the single activity flag.
 */
/**
 * WAVE GM-X — one canonical, organisation-neutral level.
 *
 * `sequence` and `rank` are NOT interchangeable and the UI must not conflate
 * them: `sequence` is the sparse ordering key an admin edits (100, 200, …) and
 * exists only to leave insertion room; `rank` is the DERIVED dense adjacency
 * number the PLUS_ONE rule compares. A major level and its sub-level share a
 * `rank` on purpose — that is what lets a level be inserted without moving
 * anybody's existing PLUS_ONE reach.
 */
export interface ReferenceGrade {
  id: string
  /** Opaque handle ("G200"). Never parsed, never sorted on. */
  code: string
  display_name: string
  /** Sparse ordering key. Never adjacency arithmetic. */
  sequence: number
  /** TRUE = a promotion step; FALSE = a sub-level sharing the major's rank. */
  is_major: boolean
  /** Dense rank over MAJOR levels — what PLUS_ONE compares. `-1` = inactive. */
  rank: number
  /** Dense position over ALL active levels — display only. */
  ordinal: number
  legacy_ladder_code: string | null
  description: string | null
  is_active: boolean
  mapped_grade_count: number
}

/** One organisation's own grade, and the canonical level it maps to. */
export interface OrganizationGrade {
  id: string
  organization_id: string
  entity_code: string | null
  reference_grade_id: string
  reference_code: string | null
  grade_code: string
  grade_name: string
  display_order: number
  is_active: boolean
}

/** The Grade Matrix — canonical level × each organisation's own vocabulary. */
export interface GradeMatrix {
  version: number
  /** Is the catalog the LIVE ranking authority, or is the legacy ladder? */
  catalog_enabled: boolean
  organizations: Array<{
    organization_id: string
    entity_code: string
    entity_name: string
    entity_type: string
  }>
  rows: Array<{
    reference_grade_id: string
    code: string
    display_name: string
    sequence: number
    is_major: boolean
    rank: number
    ordinal: number
    cells: Array<{
      organization_id: string
      entity_code: string
      entity_name: string
      grades: Array<{ id: string; grade_code: string; grade_name: string }>
    }>
  }>
  /**
   * Grades held by employees that map to NO level. Under fail-closed
   * eligibility these are INVISIBLE PEOPLE — surface them, never hide them.
   */
  unmapped: Array<{
    organization_id: string
    entity_code: string | null
    grade_code: string
    source: 'ROSTER' | 'REQUISITION'
  }>
}

/** The answer to "who moves if I insert this level?" — see `impactPreview`. */
export interface GradeImpactPreview {
  proposed: { sequence: number; is_major: boolean }
  rank_shifts: Array<{ code: string; from_rank: number; to_rank: number }>
  affected: Array<{ entity_code: string | null; grade_code: string; employees: number }>
  summary: string
}

export interface Grade {
  id: string
  entity_id: string
  /** FLOW2 F-3 (T-1): the owning entity's business code (via the entity relation). */
  entity_code: string | null
  source_grade: string
  /** FLOW2 F-3 (T-1): wire mirror of `source_grade` (always present). */
  grade_code: string
  /** FLOW2 F-3 (T-1): human-readable grade name (e.g. "Senior Manager"). */
  grade_name: string | null
  /** FLOW2 F-3 (T-1): the uploaded matrix version tag (e.g. "v2.1"). */
  matrix_version: string | null
  target_grade: string
  /**
   * Wave GM-T (T4): the wire DTO no longer emits `direction` — the key stays
   * OPTIONAL for back-compat only (legacy payloads) and is never rendered.
   */
  direction?: GradeDirection
  /** Wave GM-S: the matrix in force; a superseded row is kept, inactive, for audit. */
  is_active: boolean
  version: number
  created_at: string
}

/** Query for `GET /grades/resolve-equivalent` (FLOW2 F-3 — HR_ADMIN). */
export interface ResolveEquivalentGradeQuery {
  subsidiary_entity_id: string
  subsidiary_grade_code: string
  target_entity_id: string
}

/**
 * The resolve-equivalent output. Wave GM-S: the group-level join strategy went
 * with the column — this is now the plain pairwise `(entity, source_grade) →
 * target_grade` lookup, and the equivalent it returns is DISPLAY ONLY.
 */
export interface ResolveEquivalentGradeResult {
  subsidiary_grade_code: string
  equivalent_bank_grade_code: string
  equivalent_bank_grade_name: string | null
  matrix_version: string | null
}

export interface BulkUploadReport {
  total: number
  succeeded: number
  failed: number
  version: number
  failures: RowFailure[]
}

export interface RosterEmployee {
  id: string
  entity_id: string
  employee_code: string
  full_name?: string | null
  /** Availability signal only; the API never exposes the email address. */
  has_email?: boolean
  grade_code: string | null
  designation: string | null
  department: string | null
  location_city: string | null
  status: RosterStatus | string
  last_synced_at: string | null
}

export interface SyncBatch {
  id: string
  entity_id: string
  file_name: string | null
  file_path: string | null
  total: number
  succeeded: number
  failed: number
  status: BatchStatus | string
  started_at: string
  completed_at: string | null
  created_at: string
  /**
   * GATE D6 — plain-language explanation for a batch whose COUNTS DO NOT
   * EXPLAIN THEMSELVES (a `completed` tick that ingested nothing), else null.
   * Server-DERIVED from the row, so it can never disagree with the counts
   * beside it. Optional: older payloads predate it.
   */
  outcome_note?: string | null
}

export interface SyncBatchDetail extends SyncBatch {
  failures: RowFailure[]
}

export interface RosterSyncAccepted {
  batch_id: string
  status: 'processing'
}

export interface Eligibility {
  id: string
  entity_id: string
  roster_id: string
  employee_code: string
  eligibility_basis: EligibilityBasis
  effective_from: string
  effective_to: string | null
  hr_remarks: string | null
  upload_id: string | null
  is_active: boolean
  created_at: string
}

export interface EligibilityUpload {
  id: string
  entity_id: string
  file_name: string | null
  total: number
  succeeded: number
  failed: number
  status: BatchStatus | string
  failures: RowFailure[] | null
  version: number
  created_at: string
  created_by: string
}

export interface EligibilityUploadReport {
  upload_id: string
  total: number
  succeeded: number
  failed: number
  failures: RowFailure[]
}

/**
 * The FLOW2 F-4 structured JD object (five keys). On INPUT all keys are
 * optional; the jobs/requisitions OUTPUT normalizes them (see
 * {@link JobDescriptionSections} for the always-present detail shape).
 */
export interface JdSections {
  role_summary?: string | null
  responsibilities?: string[]
  work_with_internal?: string[]
  work_with_external?: string[]
  additional_instructions?: string | null
}

/** One FLOW2 §0.5 skill-category entry (`icon_key` may be absent/null on rows). */
export interface SkillCategory {
  name: string
  icon_key?: string | null
}

/**
 * Wave GM-R (R3) — ONE tagged SUBSIDIARY HR contact on a requisition. The email
 * is the identity (the backend lower-cases and DEDUPES on it, first occurrence
 * wins); the display name is optional. MANY per requisition, capped at
 * {@link MAX_SUBSIDIARY_HR_CONTACTS}.
 */
export interface SubsidiaryHrContact {
  email: string
  name?: string | null
}

/** Backend cap on `subsidiary_hr_contacts` (MAX_SUBSIDIARY_HR_CONTACTS). */
export const MAX_SUBSIDIARY_HR_CONTACTS = 10

/**
 * `eligibility_filters` authoring shape (FLOW2 R8): the backend accepts BOTH
 * the legacy SINGULAR keys and the FLOW2 PLURAL keys and canonicalizes to the
 * plural form on write. New UI should author the PLURAL keys; `grades` matches
 * the applicant's roster grade_code.
 */
export interface EligibilityFiltersInput {
  grades?: string[]
  departments?: string[]
  functions?: string[]
  locations?: string[]
  skills?: string[]
  /** Legacy singular vocabulary (pre-FLOW2 rows) — still accepted. */
  department?: string[]
  function?: string[]
  location?: string[]
}

/**
 * A requisition record. FLOW2 F-1: the wire emits BOTH the legacy names and
 * the new aliases (`hiring_subsidiary_id` = posting_entity_id,
 * `subsidiary_grade` = source_grade, `bank_grade` = target_grade).
 * `assigned_recruiter_id` is the recruiter's employee_code.
 */
export interface Requisition {
  id: string
  flow_type: FlowType | string
  posting_entity_id: string
  /** FLOW2 F-1 alias of `posting_entity_id` (a SUBSIDIARY on FLOW_2). */
  hiring_subsidiary_id: string
  /**
   * WAVE FB (FB-1) — THE AUTHORITY: the full target-entity SET, in the order it
   * was authored. `[]` = ENTITY-OPEN (employees of ANY company may apply).
   *
   * OPTIONAL on this type only so pre-FB-1 fixtures/payloads stay assignable —
   * the API emits it on every requisition. Read it through
   * {@link targetEntityIds}, never by falling back to the deprecated scalar
   * by hand (that would read a multi-target posting as a single-target one).
   */
  target_entity_ids?: string[]
  /**
   * @deprecated FB-1 LOSSY mirror of {@link target_entity_ids} — its FIRST id,
   * or `null` when the set is empty. The null polarity is preserved (`null`
   * still means ENTITY-OPEN in both shapes), so a stale reader sees a
   * multi-target posting as ONE of its entities: narrower than the truth,
   * never wider.
   */
  target_entity_id: string | null
  title: string
  role_name: string | null
  /**
   * WAVE M1 — the DISPLAY LABEL, and the field every render path reads. It is
   * the department master's canonical name when {@link department_code}
   * resolves, and the row's own LEGACY free text when it does not. It is never
   * blanked by the master and never a raw UPPER_SNAKE code, so a surface may
   * always render it verbatim.
   */
  department: string | null
  /**
   * WAVE M1 — the department master's `code`, or `null` for a LEGACY row whose
   * department matched no master row. The authoring picker uses it to
   * pre-select; a `null` code with a non-null {@link department} is the legacy
   * value the picker must show AS TYPED rather than blank (optional key so
   * pre-M1 fixtures stay assignable).
   */
  department_code?: string | null
  function: string | null
  location: string | null
  /** Legacy/flattened JD text (raw-extract sink). Structured copy = `jd_sections`. */
  job_description: string | null
  /** FLOW2 F-4: the structured five-key JD object (null when never authored). */
  jd_sections: JdSections | null
  // FB-8: no `jd_file_url` / `jd_file_url_presigned`. The JD document upload,
  // its object-store reference and the presigned DETAIL read went with the
  // feature (migration 0017 drops the column), and the API no longer emits
  // either field. They are NOT kept as optional-and-ignored: a declared field
  // is what lets a dead reader keep compiling.
  /**
   * FLOW2 F-4: the display Job ID.
   *
   * WAVE M3 — it is now the POSTING ENTITY'S SHORT CODE followed by six digits
   * (`AXMF435266`), not a bare 6-digit number: a job code says which company
   * owns the posting on sight. Migration 0022 rewrote every existing code and
   * archived the pre-migration value, and the lookup path still answers the old
   * bare number, so a workbook exported before the rewrite still resolves.
   *
   * RENDER IT VERBATIM. No surface parses, splits or re-prefixes it — the
   * prefix is the server's, and a client that rebuilt the string would invent a
   * second opinion about which company a posting belongs to.
   */
  job_code: string | null
  /**
   * WAVE GS (GS-A) — THE AUTHORITY, and what eligibility ranks: the SET of
   * grades this role is posted at, in the posting company's own vocabulary.
   * EMPTY = GRADE-OPEN (any grade may apply). It is a SET, not a range —
   * `['E1','E3']` does NOT admit `E2`.
   */
  source_grades: string[]
  /**
   * WAVE GS (GS-A) — the equivalent grades in the OTHER company's vocabulary.
   * Wave GM-S §9.1: DISPLAY ONLY — auto-filled from the matrix, never read by
   * eligibility (a job's rungs come from `source_grades` through the same
   * `rung()` the employee side uses).
   */
  target_grades: string[]
  /** @deprecated GS-A lossy mirror: the FIRST of `source_grades`, else null. */
  source_grade: string | null
  /** @deprecated FLOW2 F-1 alias of `source_grade`; read `source_grades`. */
  subsidiary_grade: string | null
  /** @deprecated GS-A lossy mirror: the FIRST of `target_grades`, else null. */
  target_grade: string | null
  /** @deprecated FLOW2 F-1 alias of `target_grade`; read `target_grades`. */
  bank_grade: string | null
  // Wave W4 (migration 0011): `band` is GONE — the column was dropped and the
  // field no longer rides the wire (the API REJECTS it on create/update too).
  /**
   * READ-ONLY since Wave GM-R (R2): skills live in their own normalized table
   * (`tmext_mobility_requisition_skills`, authoritative since Wave W1) and are
   * written ONLY by the RECOMMENDER, which reads the posting's job-description
   * text and extracts them through the AI gateway. Nothing authors them — they
   * are absent from every input body (the API REJECTS them). The wire shape is
   * unchanged, so cards/detail keep rendering.
   */
  skills: string[] | null
  /** READ-ONLY (GM-R R2): §0.5 skill categories `[{name, icon_key}]`. */
  skill_categories: SkillCategory[] | null
  /** READ-ONLY (GM-R R2): §0.5 skill signatures (short evidence strings). */
  skill_signatures: string[] | null
  /** FLOW2 F-4: posting origin — 'MANUAL' today. */
  source_type: RequisitionSourceType
  eligibility_filters: Record<string, unknown> | null
  recruiter_dl: string | null
  /** Assigned recruiter employee_code. */
  assigned_recruiter_id: string | null
  status: RequisitionStatus | string
  visibility_start: string | null
  visibility_end: string | null
  closed_at: string | null
  closed_reason: string | null
  // ── Wave GM-M job-master enrichment (GM-5/GM-9 — display/reference only;
  //    OPTIONAL keys so pre-GM fixtures/payloads stay assignable, the API
  //    emits them on every requisition) ───────────────────────────────────────
  sub_department?: string | null
  city?: string | null
  // Wave W4 (migration 0011): the five legacy GM-R R3 HR fields
  // (hiring_manager_id/_name, primary_hr_id/_name, additional_hr_email) are
  // GONE from the wire — the columns were dropped. Their verbatim values were
  // archived into `metadata.legacy_hr`, which the recruiter requisition detail
  // reads as the fallback for a legacy row (see `readLegacyHr` there).
  /**
   * Wave GM-R (R3) — the ONE HR concept: 0…10 SUBSIDIARY HR contacts tagged to
   * this requisition. Replaces the hiring-manager / primary-HR / additional-HR
   * trio above (optional key so pre-GM-R fixtures stay assignable).
   */
  subsidiary_hr_contacts?: SubsidiaryHrContact[] | null
  // WAVE M4: `no_of_openings` is GONE from the DTO with its column (backend
  // migration 0020) — a display-only count no rule read (FLOW rule 8), so it
  // promised a behaviour (three offers, auto-close at three) that never existed.
  position_id?: string | null
  role_id?: string | null
  /** Actor who closed the posting (soft reference; null while not CLOSED). */
  closed_by?: string | null
  /** GM-9 (D1-G1): true forces HOT_JOB on, false suppresses even derived, null = derived. */
  hot_job_override?: boolean | null
  /** GM-9 (D1-G1): true forces ACTIVELY_RECRUITING on, false suppresses, null = derived. */
  actively_recruiting_override?: boolean | null
  metadata: Record<string, unknown> | null
  created_at: string
  updated_at: string
  created_by: string
}

// FB-8: `UploadJdResult` is GONE with `POST /requisitions/:id/upload-jd` — the
// upload, the object-store write, the text extraction, the AI JD composition and
// the JD keyword skill heuristic were all removed. Requisition skills now have
// exactly ONE producer: the embedding extraction pipeline over the TYPED
// description.

/** The FLOW2 §0.4 SLIM subsidiary demarcation block (list rows). */
export interface SubsidiarySlim {
  id: string
  code: string
  name: string
  logo_url: string | null
}

/** The FULL subsidiary block (detail view adds website + about). */
export interface SubsidiaryFull extends SubsidiarySlim {
  website_url: string | null
  about: string | null
}

/**
 * Public-safe job card (employee view) — NO internal ids or eligibility logic.
 * FLOW2 F-5 additive vocabulary: `grade`/`entity_name` are the pre-FLOW2
 * aliases of `bank_grade`/`subsidiary.name`; new UI reads the NEW keys (F-12).
 */
export interface Job {
  id: string
  /** FLOW2 F-4: display Job ID (6-digit numeric string). */
  job_code: string | null
  title: string
  /** FLOW2 F-5: true when the POSTING entity is a SUBSIDIARY (S-badge/chip). */
  is_subsidiary_job: boolean
  /** FLOW2 F-5: the posting channel (derived from the posting entity's type). */
  job_channel: JobChannel
  /** FLOW2 §0.4: the branded subsidiary block; null on bank postings. */
  subsidiary: SubsidiarySlim | null
  entity_name: string
  role_name: string | null
  department: string | null
  location: string | null
  /** @deprecated GS-A lossy mirror: the FIRST of `target_grades`, else null. */
  grade: string | null
  /** @deprecated FLOW2 F-1 mirror of `grade`; read `target_grades`. */
  bank_grade: string | null
  /**
   * WAVE GS (GS-A) — THE AUTHORITY behind `grade`/`bank_grade`: every
   * counterparty grade this role is posted at. EMPTY = none named.
   */
  target_grades: string[]
  /** FLOW2 F-5: count of non-deleted applications on this posting. */
  applicant_count: number
  skills: string[]
  /** FLOW2 F-4: §0.5 skill signatures. */
  skill_signatures: string[]
  /** FLOW2 F-5: always `[]` in P0 (computeBadges seam) — contractual field. */
  badges: string[]
  visibility_end: string | null
  /** Additive server authority; absent only on pre-upgrade fixtures/responses. */
  eligibility_status?: EligibilityStatus
  eligibility_message?: string | null
  is_eligible: boolean
  ineligibility_reason: string | null
  /** True when the signed-in employee has an active bookmark. Always on live API. */
  is_bookmarked?: boolean
  /**
   * OPTIONAL skill-match score (0–100). For an EMPLOYEE principal, GET /jobs /
   * GET /jobs/all / GET /jobs/:id / GET /bookmarks now annotate it directly
   * onto each card (the SAME verdict `GET /jobs/match-scores` would answer for
   * those ids) — a redundant `useMatchScores` call still works and hits a warm
   * cache. Absent → the card renders no match badge; never render on key
   * presence, only `typeof match_percent === 'number'`.
   */
  match_percent?: number
  /** Rides alongside `match_percent` on every surface above — see it for semantics. */
  matched_skills?: string[]
  /** Rides alongside `match_percent` on every surface above — see it for semantics. */
  score_breakdown?: ScoreBreakdown
}

/** FLOW2 name for the `GET /jobs` list row (same shape as {@link Job}). */
export type JobSummary = Job

/**
 * The ALWAYS-NORMALIZED structured `job_description` object on the job DETAIL
 * (FLOW2 J-3, locked): `jd_sections` when authored, else the legacy text
 * wrapped as `{role_summary}`. Every key is present (arrays default `[]`).
 */
export interface JobDescriptionSections {
  role_summary: string | null
  responsibilities: string[]
  work_with_internal: string[]
  work_with_external: string[]
  additional_instructions: string | null
}

/** The §5 detail recruiter business-contact block (DETAIL ONLY — never on lists). */
export interface JobRecruiter {
  name: string | null
  email: string | null
  phone: string | null
  photo_url: string | null
}

export interface JobDetail extends Job {
  /** Detail upgrades the subsidiary block to the FULL shape (website + about). */
  subsidiary: SubsidiaryFull | null
  /** @deprecated FLOW2 F-1 / GS-A lossy mirror: the FIRST of `source_grades`. */
  subsidiary_grade: string | null
  /**
   * WAVE GS (GS-A) — THE AUTHORITY behind `subsidiary_grade`: the posting
   * company's own grades for this role. EMPTY = GRADE-OPEN.
   */
  source_grades: string[]
  visibility_start: string | null
  /** EXACT FLOW2 §5 process copy on SUBSIDIARY jobs; null on bank postings. */
  process_notice: string | null
  /** STRUCTURED since FLOW2 (J-3, locked) — no longer a plain string. */
  job_description: JobDescriptionSections | null
  /** FLOW2 F-4: §0.5 skill categories (icon_key null when unset). */
  skill_categories: SkillCategory[]
  /** The assigned recruiter's business contact (nulls where unknown). */
  recruiter: JobRecruiter
  recruiter_dl: string | null
}

/** Slim job card on GET /bookmarks. */
export interface BookmarkedJobCard {
  id: string
  job_code: string | null
  title: string
  status: string
  department: string | null
  location: string | null
  city: string | null
  posting_entity_id: string
  posting_entity_name: string | null
  role_name: string | null
  is_currently_visible: boolean
}

export interface BookmarkedJobItem {
  job_id: string
  bookmarked_at: string
  job: BookmarkedJobCard | null
}

export interface BookmarkMutation {
  job_id: string
  bookmarked: boolean
  bookmarked_at: string | null
}

/** The `for_matching=true` LEAN list row (FLOW2 §5 — AI-gateway payload). */
export interface JobMatchingItem {
  id: string
  job_code: string | null
  title: string
  skills: string[]
  skill_signatures: string[]
  department: string | null
  location: string | null
  subsidiary_code: string | null
  status: string
}

/**
 * The §0.5 canonical skill payload. `job_id` and `requisition_id` are BOTH the
 * requisition UUID (a mobility job IS the requisition, J-11).
 */
export interface JobSkillPayload {
  job_id: string
  requisition_id: string
  title: string
  skills: string[]
  skill_categories: SkillCategory[]
  skill_signatures: string[]
  department: string | null
  location: string | null
  subsidiary_code: string | null
  status: string
}

export interface ApplicationStatusHistoryItem {
  from_status: string | null
  to_status: string
  reason: string | null
  actor_id: string
  created_at: string
}

/** The FLOW2 §6 embedded branded `job{...}` block (201 + employee list items). */
export interface ApplicationJobBlock {
  id: string
  title: string
  job_code: string | null
  is_subsidiary_job: boolean
  job_channel: JobChannel
  subsidiary: SubsidiarySlim | null
  role_name: string | null
  department: string | null
  location: string | null
}

export interface Application {
  id: string
  requisition_id: string
  roster_id: string
  entity_id: string
  employee_code: string
  current_status: string
  closure_reason: string | null
  /** FLOW2 F-7: staff-authored, employee-facing closing message. */
  closure_message: string | null
  applied_at: string
  updated_at: string
  // ── FLOW2 F-7 EMPLOYEE-branch enrichment (ABSENT on the staff list view) ────
  /** The catalog row's label for `current_status` (employee list only). */
  status_label?: string | null
  /** "Full Name (employee_code)" of the assigned recruiter, else the recruiter DL. */
  recruiter_display?: string | null
  /**
   * The embedded branded job block (kills the FE client-side join).
   * AD-4: `GET /applications/:id` now carries it on the STAFF branch too, so
   * the recruiter drawer can name the posting under review. Still absent from
   * the staff LIST rows — hence optional here as well as nullable (null when
   * the requisition ref is unresolvable).
   */
  job?: ApplicationJobBlock | null
  // ── MP-4 apply-time ADVISORY stamp (STAFF list/detail views ONLY — absent on
  //    every employee-facing surface, which reads live scores via
  //    GET /jobs/match-scores instead) ────────────────────────────────────────
  /**
   * ADVISORY (Posture B, D-2): the 0–100 skill-match score frozen at apply
   * time. Display/context only — it never gates, filters, or orders anything.
   */
  ai_score?: number | null
  /** MP-4: 'SCORED' | 'UNAVAILABLE' (null before the stamp path existed). Advisory. */
  skill_match_status?: string | null
}

export interface ApplicationDetail extends Application {
  /** FLOW2 F-7: the applicant's skill-alignment note (detail surfaces only). */
  skill_alignment_comment: string | null
  /** Wave GM-M (GM-4): YYYY-MM-DD joining date recorded with OFFERED/JOINED. */
  joining_date: string | null
  /** Wave GM-M (GM-4): staff-recorded selection feedback (verbatim, ≤1000). */
  selection_feedback: string | null
  /** Wave GM-M (GM-8): true when the auto-close cron closed the row. */
  is_auto_closed: boolean
  /** Wave GM-M (GM-8): the auto-close instant (ISO-8601); null unless auto-closed. */
  auto_closed_at: string | null
  official_email: string | null
  mobile_number: string | null
  additional_contact: Record<string, unknown> | null
  profile_snapshot: Record<string, unknown> | null
  consent_given: boolean
  consent_timestamp: string | null
  status_history: ApplicationStatusHistoryItem[]
}

/**
 * The FLOW2 §6 apply-form prefill (`GET /applications/prefill`) — OWNER only:
 * current employee-source email/mobile + the NON-PII profile-summary card.
 * Missing current mobile remains null for the employee to fill.
 */
export interface ApplicationPrefill {
  requisition_id: string
  official_email: string | null
  mobile_number: string | null
  additional_contact: Record<string, unknown>
  profile_summary: {
    employee_code: string
    full_name: string | null
    grade_code: string | null
    department: string | null
    location_city: string | null
  }
}

export interface SubmitApplicationResult {
  id: string
  status: string
  applied_at: string
  /** FLOW2 §6: the branded job block (null when the requisition ref is unresolvable). */
  job: ApplicationJobBlock | null
  /** FLOW2 §6: "Your application has been submitted to the <entity> HR team." */
  success_message: string
  /** FLOW2 §6: the exact next-steps copy for the success panel. */
  next_steps_notice: string
}

export interface ExportApplicationsResult {
  download_url: string
  /** FLOW2 F-7: ISO expiry of the presigned `download_url`. */
  expires_at: string
  count: number
  /** Echoes the rendered format (GM-M S2: `xlsx` = Application_DUMP; default `pdf`). */
  format: ExportApplicationsFormat
  /**
   * How many DISTINCT requisitions the file covers — SERVER-DERIVED. A surface
   * may say "6 applications across 3 requisitions" from this and nothing else;
   * it must never infer the span from the rows it happens to be showing.
   * Optional for wire compatibility with pre-spanning responses.
   */
  requisition_count?: number
  /** The SAVE-AS name of the download; it states its own scope. */
  file_name?: string
}

export interface StatusCatalogItem {
  id: string
  status_code: string
  status_label: string
  status_type: StatusType
  display_order: number
  requires_reason: boolean
  /**
   * WAVE M4 — the sibling of {@link requires_reason}: TRUE ⇒ a move INTO this
   * status must carry a joining date (the server refuses it otherwise). The
   * dialog reads THIS, never a hardcoded 'OFFERED', so the UI can never drift
   * from the config the server enforces. OPTIONAL so pre-M4 fixtures still
   * typecheck; absent reads as NOT required, which is the server's own default.
   */
  requires_joining_date?: boolean
  /**
   * AD-1 — the third sibling: TRUE ⇒ only the APPLICANT may move an application
   * into this status, so a STAFF menu must not offer it (seeded TRUE on
   * WITHDRAWN). Read here rather than hardcoding 'WITHDRAWN' for the same
   * reason as above — and note the menu is a COURTESY, not the gate: the server
   * refuses the transition regardless, so a stale client is refused, never
   * obeyed. OPTIONAL; absent reads as NOT restricted, the server's own default.
   */
  requires_owner?: boolean
  closure_reasons: string[] | null
}

// ── WAVE M6: the status-catalog ADMIN surface ────────────────────────────────

/**
 * A catalog row as the admin console sees it — the staff entry plus the active
 * flag and the USAGE COUNTS that decide which edits are legal.
 *
 * The counts are on the row for a reason: `current_status` is a plain string on
 * the application with no foreign key, so the server REFUSES a rename once
 * anything references the code, and refuses a delete while anyone is still in
 * it. Reading the counts here lets the UI disable those controls up front
 * rather than offering them and failing at Save.
 */
export interface StatusCatalogAdminItem extends StatusCatalogItem {
  is_active: boolean
  /** Applications sitting in this status RIGHT NOW. Blocks delete. */
  live_applications: number
  /** Append-only history rows naming it. Together with the above, blocks rename. */
  historical_references: number
  created_at: string
  updated_at: string
}

export interface CreateStatusCatalogInput {
  status_code: string
  status_label: string
  status_type: StatusType
  /** Omitted ⇒ appended to the end of the ladder. */
  display_order?: number
  requires_reason?: boolean
  requires_joining_date?: boolean
  requires_owner?: boolean
  closure_reasons?: string[] | null
}

export interface UpdateStatusCatalogInput {
  id: string
  /** Only legal while the code has NEVER been used — else change the label. */
  status_code?: string
  status_label?: string
  status_type?: StatusType
  display_order?: number
  requires_reason?: boolean
  requires_joining_date?: boolean
  requires_owner?: boolean
  /** `null` CLEARS the list (any free-text reason); omit to leave it untouched. */
  closure_reasons?: string[] | null
  is_active?: boolean
  /**
   * The caller has SEEN the impact preview and accepts it. Required for the one
   * edit that strands live applications: turning a status TERMINAL while people
   * are sitting in it (a terminal status has no exits, so they freeze).
   */
  acknowledge_impact?: boolean
}

export interface StatusImpactPreviewInput {
  id: string
  status_type?: StatusType
  is_active?: boolean
  /** Preview a soft-delete rather than an edit. */
  deleting?: boolean
}

/** What a proposed status edit would do to live data. Renders the confirm dialog. */
export interface StatusImpactResult {
  status_code: string
  status_label: string
  live_applications: number
  historical_references: number
  /** Plain-language consequences, ready to list. */
  effects: string[]
  /** TRUE ⇒ the server WILL refuse this write; `block_reason` says why. */
  blocked: boolean
  block_reason: string | null
  /** TRUE ⇒ the write needs `acknowledge_impact`. */
  requires_acknowledgement: boolean
}

export interface RoleGrant {
  id: string
  roster_id: string
  entity_id: string
  role: Role
  granted_by: string
  granted_at: string
  revoked_at: string | null
  is_active: boolean
  created_at: string
}

export interface Setting {
  id: string
  setting_key: string
  setting_value: string
  value_type: SettingValueType
  category: string
  is_mutable: boolean
  is_sensitive: boolean
  description: string | null
  /** Present ⇒ a closed set of legal values; the admin UI renders a picker
   * over exactly this list instead of free text. Absent/null ⇒ unconstrained. */
  allowed_values: string[] | null
}

export interface OllamaModelStatus {
  configured: boolean
  ready: boolean
  endpoint: string | null
  model: string
  dimensions: number
  enabled: boolean
  timeout_ms: number
  max_parallel: number
  max_inputs: number
  installed_models: string[]
  last_embedded_model: string | null
  error: string | null
}

export interface OllamaModelCheck {
  ok: boolean
  model: string | null
  dimensions: number | null
  latency_ms: number
  error: string | null
}

// ── Request-body / query input shapes ────────────────────────────────────────────

export interface ListQuery {
  page?: number
  limit?: number
  sort_by?: string
  sort_order?: 'asc' | 'desc'
}

export interface CreateEntityInput {
  /**
   * WAVE M7 — OPTIONAL. Omit it and the server DERIVES it from `entity_name`
   * (`Axis Mutual Fund` → `AXIS_MUTUAL_FUND`), disambiguated against the live
   * codes. The admin create form omits it; an import that must match an
   * upstream system still pins it.
   */
  entity_code?: string
  /** M3 — the job-code prefix (uppercase alphanumeric, 2-8 chars, unique). */
  short_code?: string | null
  entity_name: string
  entity_type: EntityType
  domain?: string | null
  hr_spoc_email?: string | null
  /** WAVE M7 — the SECONDARY HR contacts (max 20). */
  hr_contacts?: HrContactDTO[]
  /** FLOW2 F-2 branding (absolute http(s) URL, ≤1024 chars). */
  logo_url?: string | null
  /** FLOW2 F-2 branding (absolute http(s) URL, ≤512 chars). */
  website_url?: string | null
  /** FLOW2 F-2 branding (free text, ≤10000 chars). */
  about?: string | null
  metadata?: Record<string, unknown>
}

/** Partial update: absent = unchanged; explicit `null` CLEARS a nullable field. */
export interface UpdateEntityInput {
  entity_code?: string
  /** M3 — explicit `null` CLEARS the job-code prefix (new codes then refuse). */
  short_code?: string | null
  entity_name?: string
  entity_type?: EntityType
  domain?: string | null
  hr_spoc_email?: string | null
  /** WAVE M7 — REPLACES the secondary list; `[]` clears it. */
  hr_contacts?: HrContactDTO[]
  logo_url?: string | null
  website_url?: string | null
  about?: string | null
  is_active?: boolean
  metadata?: Record<string, unknown>
}

/**
 * Create body (FLOW2 F-1 aliases are ADDITIVE): send `hiring_subsidiary_id`
 * OR the legacy `posting_entity_id` (never both — the backend 400s);
 * `target_entity_id` may be omitted on FLOW_2 (inferred as the BANK entity).
 * Same either/or rule for `subsidiary_grade`/`source_grade` and
 * `bank_grade`/`target_grade`. `assigned_recruiter_id` is an employee_code.
 */
export interface CreateRequisitionInput {
  /**
   * Wave GM-T (T1): OPTIONAL — the backend DERIVES the flow from the posting
   * entity's type (SUBSIDIARY → FLOW_2, BANK → FLOW_1). An explicit value is
   * still accepted (back-compat) but the dialog no longer sends one.
   */
  flow_type?: FlowType
  posting_entity_id?: string
  /** FLOW2 F-1 alias of posting_entity_id — MUST be a SUBSIDIARY on FLOW_2. */
  hiring_subsidiary_id?: string
  /**
   * WAVE FB (FB-1) — the MULTI-SELECT target, and the ONE field new clients
   * send. `[]` is legal and meaningful: it says ENTITY-OPEN explicitly (any
   * company's employees may apply) and suppresses the backend's legacy BANK
   * inference. On UPDATE the array REPLACES the stored set.
   *
   * Sending this AND {@link target_entity_id} together is a 400.
   */
  target_entity_ids?: string[]
  /**
   * @deprecated FB-1 legacy single-target alias — `X` is read as `[X]`, and on
   * UPDATE an explicit `null` clears the set. Kept so pre-FB-1 callers keep
   * working; nothing in this app sends it any more.
   */
  target_entity_id?: string | null
  title: string
  role_name?: string | null
  /**
   * WAVE M1 — accepted as EITHER a master code OR a name (case-insensitive).
   * A hit stores the master's canonical label + code; a MISS stores the text
   * verbatim as LEGACY free text, so nothing an author already typed is
   * rejected. A picker should prefer {@link department_code}.
   */
  department?: string | null
  /**
   * WAVE M1 — an EXPLICIT department-master code (`GET /departments`). STRICT,
   * unlike {@link department}: an unknown code is a 400, because a client that
   * opted into the controlled vocabulary and then sent a code the master does
   * not have is a bug, not a legacy row. Send at most one of the pair; this one
   * wins if both arrive.
   */
  department_code?: string | null
  function?: string | null
  location?: string | null
  /**
   * Legacy string OR the FLOW2 structured object (object → jd_sections).
   *
   * WAVE M2 — OMIT THIS ENTIRELY when {@link role_id} resolves against the role
   * master: the master OWNS that posting's JD, and the server REFUSES a body
   * that also carries one (400, never a silent drop) so two people can never
   * end up disagreeing about what the posting says.
   */
  job_description?: string | JdSections | null
  /**
   * WAVE GS (GS-A) — the MULTI-SELECT posted grades (the eligibility input).
   * `[]` (or omitting it) is GRADE-OPEN. Send THIS, not the singulars: the API
   * REJECTS a body carrying both a plural and its singular alias.
   */
  source_grades?: string[]
  /** WAVE GS (GS-A) — the DISPLAY-ONLY counterpart set; `[]` clears it. */
  target_grades?: string[]
  /** @deprecated GS-A legacy single-grade alias of `source_grades`. */
  source_grade?: string | null
  /** @deprecated FLOW2 F-1 alias of source_grade. */
  subsidiary_grade?: string | null
  /** @deprecated GS-A legacy single-grade alias of `target_grades`. */
  target_grade?: string | null
  /** @deprecated FLOW2 F-1 alias of target_grade. */
  bank_grade?: string | null
  // Wave GM-R (R2): `skills` / `skill_categories` / `skill_signatures` are GONE
  // from this body. Skills are normalized into their own table and written ONLY
  // by the recommender (from the JD text, via the AI gateway) — sending any of
  // the three is a hard VALIDATION_ERROR, never a silent strip. Read them off
  // {@link Requisition}; re-run extraction with {@link extractRequisitionSkills}.
  /** FLOW2 F-4: posting origin — only 'MANUAL' is accepted today (create only). */
  source_type?: 'MANUAL'
  /** Typed both-vocabularies shape (canonicalized to plural keys on write). */
  eligibility_filters?: EligibilityFiltersInput
  recruiter_dl?: string | null
  /** Assigned recruiter employee_code. */
  assigned_recruiter_id?: string | null
  // ── Wave GM-M enrichment authoring (D1-G2 — manual parity with the JOBS
  //    import; display/reference only, NO headcount logic) ────────────────────
  sub_department?: string | null
  city?: string | null
  // Wave GM-R (R3): the hiring-manager / primary-HR / additional-HR trio is GONE
  // from this body — HR tagging is ONE concept now, the list below. The legacy
  // columns are still READ on {@link Requisition} for pre-GM-R rows.
  /**
   * Wave GM-R (R3) — the SUBSIDIARY HR contacts tagged to this requisition
   * (MANY per requisition, ≤ {@link MAX_SUBSIDIARY_HR_CONTACTS}, deduped on the
   * lower-cased email). On UPDATE the array REPLACES the stored list; `[]` clears it.
   */
  subsidiary_hr_contacts?: SubsidiaryHrContact[]
  // WAVE M4: `no_of_openings` LEFT the write contract with its column.
  position_id?: string | null
  role_id?: string | null
  /**
   * Wave GM-R (R5): the PLANNED visibility window start (YYYY-MM-DD), authorable
   * at create/DRAFT-edit. Publish still resolves the EFFECTIVE window (explicit
   * publish dates > these > the M-17 defaults).
   */
  visibility_start?: string
  /** Wave GM-R (R5): the PLANNED window end (YYYY-MM-DD) — same precedence rule. */
  visibility_end?: string
  // ── GM-9 badge overrides (D1-G1) — tri-state: true forces the badge, false
  //    suppresses even the derived badge, explicit null returns to DERIVED.
  //    OPEN-editable (the R12 whitelist admits them).
  hot_job_override?: boolean | null
  actively_recruiting_override?: boolean | null
  metadata?: Record<string, unknown>
}

export type UpdateRequisitionInput = Partial<CreateRequisitionInput> & {
  /** FLOW2 R12: OPEN-phase correction — extend/trim the window end (YYYY-MM-DD). */
  visibility_end?: string
}

/** POST /requisitions/:id/assign-recruiter (FLOW2 R4) — at least one field. */
export interface AssignRecruiterInput {
  /** The recruiter's employee_code; explicit `null` CLEARS the assignment. */
  assigned_recruiter_id?: string | null
  recruiter_dl?: string | null
}

export interface PublishRequisitionInput {
  visibility_start?: string
  visibility_end?: string
}

// ── WAVE M6: close / hold / reactivate ───────────────────────────────────────

/** One application in a closure bucket. NON-PII — an employee CODE. */
export interface ClosureCandidate {
  id: string
  employee_code: string
  current_status: string
}

/**
 * Who is on this posting right now. One shape serves close and hold, but the
 * two read it differently: the buckets below are named for what a CLOSE would
 * do, and a hold — which rejects nobody — treats the whole preview as one
 * preserved list.
 */
export interface ClosurePreview {
  requisition_id: string
  job_code: string | null
  title: string
  status: string
  /**
   * On a CLOSE these WILL be rejected; echo the length back as
   * `expected_active_count`. On a hold they are kept, like everyone else.
   */
  will_reject: ClosureCandidate[]
  /** Extended offers — never swept, even by a close. Tell the user to handle them. */
  protected: ClosureCandidate[]
  already_closed: number
}

export interface StopRequisitionInput {
  /** INTERNAL: the business record of why the posting stopped. Mandatory. */
  reason: string
  /** EMPLOYEE-FACING: written onto every application the sweep closes. CLOSE only — a hold closes none, and ignores this. */
  message?: string
  /**
   * CLOSE only: the `will_reject.length` the user was shown. A mismatch is a
   * 409 — they authorized rejecting a specific set of people, not a query. A
   * hold rejects nobody, so it neither sends this nor is checked on it.
   */
  expected_active_count?: number
}

/** What a close or hold actually did. */
export interface ClosureResult {
  requisition: Requisition
  /** Applications the sweep closed. Structurally 0 for a hold. */
  rejected: number
  /**
   * Rows the sweep could not move. The posting is stopped regardless, so this
   * means "N people were not told" — surface it, and re-running drains them.
   */
  failed: number
  /**
   * Applications left standing: for a close, the exempt offers; for a hold, the
   * ENTIRE pipeline it preserved. Quote it back — on a hold it is the only
   * number that describes what happened.
   */
  protected: number
}

export interface ReactivateRequisitionInput {
  /** MANDATORY: the board checks the dates too, so a stale window stays invisible. */
  visibility_end: string
  /** Defaults to today (UTC). */
  visibility_start?: string
}

/**
 * Apply body — contacts are EDITABLE (prefill seeds them; the employee may
 * correct both before submit). `Idempotency-Key` may ride `options.headers`.
 */
export interface SubmitApplicationInput {
  requisition_id: string
  official_email: string
  mobile_number: string
  additional_contact?: Record<string, unknown>
  /** FLOW2 F-7: optional skill-alignment note — HARD 320-char cap. */
  skill_alignment_comment?: string
  consent_given: boolean
}

/** POST /applications/:id/withdraw — optional; omit for a one-click withdraw. */
export interface WithdrawApplicationInput {
  /** Optional free-text note (≤500). Stored as `closure_message`. */
  message?: string
}

export interface UpdateApplicationStatusInput {
  status: string
  /** Required by the catalog for TERMINAL targets. */
  reason?: string
  /**
   * FLOW2 F-7: optional EMPLOYEE-FACING closing message (≤500 chars) —
   * accepted ONLY when the target status is TERMINAL (else VALIDATION_ERROR);
   * stored as `closure_message`.
   */
  message?: string
  /**
   * Wave GM-M (GM-4): YYYY-MM-DD joining date — accepted ONLY with an
   * OFFERED/JOINED target (else VALIDATION_ERROR); stored as `joining_date`.
   */
  joining_date?: string
  /** Wave GM-M (GM-4): optional staff selection feedback (stored verbatim, ≤1000). */
  selection_feedback?: string
}

export interface ExportApplicationsInput {
  /**
   * OPTIONAL. Absent ⇒ the CROSS-REQUISITION export: the backend scopes it to
   * exactly the applications this principal can LIST under the filters below
   * (its own `GET /applications` predicate — never wider), capped and REFUSED
   * past the cap rather than truncated.
   */
  requisition_id?: string | null
  application_ids?: string[] | null
  /**
   * Wave GM-M (S2): `xlsx` = the counterparty's Application_DUMP workbook (the
   * exact Template_GM 15-column dictionary); absent/null ⇒ `pdf` (unchanged
   * default — the backend schema is `z.enum(['pdf','xlsx']).nullish()`).
   */
  format?: ExportApplicationsFormat | null
  /** The list's `status` filter — a catalog status code. */
  status?: string | null
  /**
   * The list's `entity_id` filter — the APPLICANT's current company. SAME wire
   * spelling as `GET /applications`: one uuid or a CSV of them, so the console
   * sends the filter it is already showing rather than a second dialect of it.
   */
  entity_id?: string | null
}

export interface GrantRoleInput {
  roster_id: string
  entity_id: string
  role: Role
}

// ── Query helpers ────────────────────────────────────────────────────────────────

/** Build a `?a=1&b=2` string from a filter object, dropping undefined/null/empty. */
function qs(params: object): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params) as [string, unknown][]) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      for (const item of value) if (item !== '') search.append(key, String(item))
      continue
    }
    search.set(key, String(value))
  }
  const s = search.toString()
  return s ? `?${s}` : ''
}

const c = mobilityClient

export type JobsListQuery = ListQuery & {
  /** Backward-compatible broad search over title, job code and role name. */
  keyword?: string
  /** Explicit frontend search text; preferred over `keyword` when both exist. */
  search?: string
  /** Restrict `search` to one source field. */
  searchBy?: 'job_title' | 'job_id'
  location?: string
  /** Repeated query values: OR within the selected location facet. */
  locations?: string[]
  department?: string
  channel?: JobChannel
  subsidiary_id?: string
  /** Posting entity UUIDs selected in the multi-select facet. */
  posting_entity_ids?: string[]
  eligible_only?: boolean
  /** Index is the server default; `db` and legacy `sql` force Postgres. */
  engine?: 'db' | 'sql' | 'index'
  semantic?: boolean
}

// ── The typed api surface (one function per endpoint) ────────────────────────────

export const mobilityApi = {
  // ── Entities (API_SPEC §2 + FLOW2 F-2) ────────────────────────────────────────
  entities: {
    /**
     * Staff view — the FULL DTO. NOTE: `is_active` DEFAULTS TO TRUE server-side
     * (FLOW2 E3); pass `is_active: false` explicitly to see inactive entities,
     * or the literal `'all'` (WAVE F3, the grades module's spelling) to drop the
     * filter and list both — what the admin console's "All entities" option
     * sends, and the only way to see a deactivated row in order to reactivate it.
     */
    list: (
      filters: ListQuery & { is_active?: boolean | 'all'; entity_type?: EntityType } = {},
      options?: RequestOptions,
    ): Promise<PagedResponse<Entity[]>> => c.getPage<Entity[]>(`/entities${qs(filters)}`, options),
    /**
     * WAVE M7 — what the create form should pre-fill for a company with this
     * name. NON-MUTATING and reserves nothing: two admins typing the same name
     * see the same answer and the second loses at CREATE, which is the only
     * place that can be decided atomically.
     */
    suggestCodes: (name: string, options?: RequestOptions): Promise<SuggestedEntityCodes> =>
      c.get<SuggestedEntityCodes>(`/entities/suggest-codes${qs({ name })}`, options),
    /**
     * EMPLOYEE view of the SAME endpoint — the server projects the SLIM shape
     * (identity + branding) for EMPLOYEE callers (FLOW2 F-2). Use this typing
     * on employee surfaces (subsidiary dropdowns, About blocks).
     */
    listSlim: (
      filters: ListQuery & { is_active?: boolean; entity_type?: EntityType } = {},
      options?: RequestOptions,
    ): Promise<PagedResponse<SlimEntity[]>> =>
      c.getPage<SlimEntity[]>(`/entities${qs(filters)}`, options),
    create: (input: CreateEntityInput, options?: RequestOptions): Promise<Entity> =>
      c.post<Entity>('/entities', input, options),
    update: (id: string, input: UpdateEntityInput, options?: RequestOptions): Promise<Entity> =>
      c.post<Entity>(`/entities/${id}/update`, input, options),
  },

  // ── Reference-grade catalog (Wave GM-X) ──────────────────────────────────────
  //
  // The CANONICAL ladder that replaced Axis Bank's own vocabulary as the pivot.
  // `grades` below is the LEGACY per-entity mapping surface and still backs the
  // upload path; this is the surface that renders the actual Grade Matrix.
  referenceGrades: {
    /** The Grade Matrix: one row per canonical level, one cell per organisation. */
    matrix: (options?: RequestOptions): Promise<GradeMatrix> =>
      c.get<GradeMatrix>('/grade-matrix', options),
    /** The canonical ladder, junior → senior. */
    list: (
      filters: ListQuery & { is_active?: boolean | 'all' } = {},
      options?: RequestOptions,
    ): Promise<PagedResponse<ReferenceGrade[]>> =>
      c.getPage<ReferenceGrade[]>(`/reference-grades${qs(filters)}`, options),
    /** One organisation's own grades and the levels they map to. */
    organizationGrades: (
      filters: ListQuery & { organization_id?: string; reference_grade_id?: string } = {},
      options?: RequestOptions,
    ): Promise<PagedResponse<OrganizationGrade[]>> =>
      c.getPage<OrganizationGrade[]>(`/organization-grades${qs(filters)}`, options),
    /**
     * NON-MUTATING. Ask who would be affected BEFORE inserting a level: a MAJOR
     * insertion re-ranks everything above it and can remove a whole cohort's
     * PLUS_ONE reach with no code change and no error. A sub-level re-ranks
     * nothing — which is why it is the default.
     */
    impactPreview: (
      input: { sequence?: number; insert_after_code?: string; is_major?: boolean },
      options?: RequestOptions,
    ): Promise<GradeImpactPreview> =>
      c.post<GradeImpactPreview>('/reference-grades/impact-preview', input, options),
    /**
     * Create a canonical level. Defaults to a SUB-LEVEL (shares the major's
     * rank, so nobody's PLUS_ONE reach moves). Pass `is_major: true` only after
     * `impactPreview` — a major insertion re-ranks everything above it.
     * Place it with `insert_after_code` (server picks the midpoint) unless the
     * ladder is empty, in which case `sequence` starts it.
     */
    createReferenceGrade: (
      input: {
        code: string
        display_name: string
        sequence?: number
        insert_after_code?: string
        is_major?: boolean
        description?: string
      },
      options?: RequestOptions,
    ): Promise<ReferenceGrade> => c.post<ReferenceGrade>('/reference-grades', input, options),
    /** Rename, flip major/sub-level, or retire. The code and sequence do not move. */
    updateReferenceGrade: (
      input: {
        id: string
        display_name?: string
        description?: string | null
        is_active?: boolean
        is_major?: boolean
      },
      options?: RequestOptions,
    ): Promise<{ ok: true }> => c.post<{ ok: true }>('/reference-grades/update', input, options),
    /** Soft-delete a level that no organisation grade still maps to. */
    deleteReferenceGrade: (id: string, options?: RequestOptions): Promise<{ ok: true }> =>
      c.post<{ ok: true }>('/reference-grades/delete', { id }, options),
    /**
     * Housekeeping: stretch sequence gaps back to 1000 so a later insert has
     * room. Changes no mapping and no eligibility outcome — only the sparse
     * keys. Needed when an insert is refused because two neighbours have no
     * integer between them.
     */
    resequence: (options?: RequestOptions): Promise<{ renumbered: number }> =>
      c.post<{ renumbered: number }>('/reference-grades/resequence', {}, options),
    /**
     * Upload the matrix sheet (multipart, field `file`) — CSV or .xlsx.
     * Columns: `entity_code`, `source_grade` (the organisation's own grade) and
     * `reference_grade` (the canonical level it is equivalent to), plus optional
     * `grade_name` / `display_order`. Partial success: good rows commit, bad
     * rows come back numbered.
     */
    bulkUpload: (file: File, options?: RequestOptions): Promise<BulkUploadReport> => {
      const form = new FormData()
      form.append('file', file)
      return c.postMultipart<BulkUploadReport>('/organization-grades/bulk-upload', form, options)
    },
    /**
     * Map one organisation's own grade onto a canonical level. Duplicate
     * `(organization, grade_code)` is refused — one grade maps to exactly one
     * level. Bumps the catalog version so ranking picks the change up.
     */
    createOrganizationGrade: (
      input: {
        organization_id: string
        reference_grade_id: string
        grade_code: string
        grade_name: string
        display_order?: number
      },
      options?: RequestOptions,
    ): Promise<{ ok: true }> => c.post<{ ok: true }>('/organization-grades', input, options),
    /**
     * Rename, remap to another level, or retire. The grade CODE and owning
     * organisation are immutable — the roster keys on them, so a typo is a
     * delete-and-recreate, not an edit.
     */
    updateOrganizationGrade: (
      input: {
        id: string
        reference_grade_id?: string
        grade_name?: string
        display_order?: number
        is_active?: boolean
      },
      options?: RequestOptions,
    ): Promise<{ ok: true }> => c.post<{ ok: true }>('/organization-grades/update', input, options),
    /** Soft-delete a mapping. Employees on this grade become unmapped (fail closed). */
    deleteOrganizationGrade: (id: string, options?: RequestOptions): Promise<{ ok: true }> =>
      c.post<{ ok: true }>('/organization-grades/delete', { id }, options),
  },

  // ── Grade matrix (API_SPEC §3 + FLOW2 F-3) ────────────────────────────────────
  grades: {
    /**
     * Wave GM-S: the `effective_date` window filter went with the columns. The
     * server defaults to the matrix IN FORCE (`is_active=true`); the literal
     * `'all'` is the explicit history escape that also lists superseded rows.
     */
    list: (
      filters: ListQuery & {
        entity_id?: string
        /** GM-T T4: still accepted server-side (legacy directed rows) — no UI sends it. */
        direction?: GradeDirection
        is_active?: boolean | 'all'
      } = {},
      options?: RequestOptions,
    ): Promise<PagedResponse<Grade[]>> => c.getPage<Grade[]>(`/grades${qs(filters)}`, options),
    /**
     * GET /grades/resolve-equivalent (FLOW2 F-3/G2 — HR_ADMIN): resolve a
     * subsidiary grade to its bank equivalent — Wave GM-S: a plain pairwise
     * `(entity, source_grade) → target_grade` lookup (the group_level join
     * strategy is gone). 404 NOT_FOUND when no mapping exists.
     */
    resolveEquivalentGrade: (
      query: ResolveEquivalentGradeQuery,
      options?: RequestOptions,
    ): Promise<ResolveEquivalentGradeResult> =>
      c.get<ResolveEquivalentGradeResult>(`/grades/resolve-equivalent${qs(query)}`, options),
    /**
     * Bulk upload (multipart, field name `file`) — CSV **or .xlsx** (Wave
     * GM-M S1: content-sniffed server-side; a workbook prefers its
     * GRADE_MATRIX sheet). The backend SNIFFS the header row and accepts the
     * legacy matrix format (`entity_code, source_grade, target_grade`), CSV
     * Template T-1 (`entity_code, grade_code, grade_name, matrix_version`)
     * AND the Template_GM 4-column GRADE_MATRIX sheet. No client-side
     * extension/content-type restriction — pass the File as picked. Wave GM-S:
     * the retired `band` / `group_level` / `effective_from` / `effective_to`
     * columns are TOLERATED AND IGNORED in every template, so unchanged
     * counterparty and legacy files keep importing.
     */
    bulkUpload: (file: File, options?: RequestOptions): Promise<BulkUploadReport> => {
      const form = new FormData()
      form.append('file', file)
      return c.postMultipart<BulkUploadReport>('/grades/bulk-upload', form, options)
    },
  },

  // ── Roster (API_SPEC §4) ──────────────────────────────────────────────────────
  roster: {
    list: (
      filters: ListQuery & {
        entity_id?: string
        status?: RosterStatus
        grade_code?: string
        department?: string
        keyword?: string
      } = {},
      options?: RequestOptions,
    ): Promise<PagedResponse<RosterEmployee[]>> =>
      c.getPage<RosterEmployee[]>(`/roster${qs(filters)}`, options),
    sync: (entity_id: string, force = false, options?: RequestOptions): Promise<RosterSyncAccepted> =>
      c.post<RosterSyncAccepted>('/roster/sync', { entity_id, force }, options),
    batches: (
      filters: ListQuery & { entity_id?: string; status?: BatchStatus } = {},
      options?: RequestOptions,
    ): Promise<PagedResponse<SyncBatch[]>> =>
      c.getPage<SyncBatch[]>(`/roster/sync/batches${qs(filters)}`, options),
    batch: (batchId: string, options?: RequestOptions): Promise<SyncBatchDetail> =>
      c.get<SyncBatchDetail>(`/roster/sync/batches/${batchId}`, options),
  },

  // ── Eligibility (API_SPEC §5) ─────────────────────────────────────────────────
  eligibility: {
    /**
     * Allow-list upload (multipart, field `file`) — CSV **or .xlsx** (Wave
     * GM-M S1: the raw buffer is forwarded and content-sniffed server-side;
     * the Template_GM 5-column ELIGIBILITY sheet is accepted alongside the
     * legacy CSV). No client-side extension/content-type restriction.
     */
    upload: (file: File, options?: RequestOptions): Promise<EligibilityUploadReport> => {
      const form = new FormData()
      form.append('file', file)
      return c.postMultipart<EligibilityUploadReport>('/eligibility/upload', form, options)
    },
    list: (
      filters: ListQuery & { entity_id?: string; employee_code?: string; is_active?: boolean } = {},
      options?: RequestOptions,
    ): Promise<PagedResponse<Eligibility[]>> =>
      c.getPage<Eligibility[]>(`/eligibility${qs(filters)}`, options),
    uploads: (
      filters: ListQuery & { entity_id?: string; status?: BatchStatus } = {},
      options?: RequestOptions,
    ): Promise<PagedResponse<EligibilityUpload[]>> =>
      c.getPage<EligibilityUpload[]>(`/eligibility/uploads${qs(filters)}`, options),

    /**
     * ⚠️ MOCK — pull the IJP base from the upstream IJP system.
     *
     * The real thing is a server-side job: mobility calls IJP, reconciles every
     * employee, and writes the allow-list. NONE of that exists yet, and it
     * cannot be faked usefully from the browser — a client cannot reach a
     * third-party system it has no credentials for, and must not be given any.
     *
     * So this resolves after a beat and reports nothing it does not know. The
     * caller re-reads the list afterwards, which is exactly what it will do when
     * this becomes real; when the endpoint lands, ONLY this function body
     * changes:
     *
     *     syncFromIjp: (options) => c.post<IjpSyncReport>('/eligibility/sync', {}, options)
     *
     * It deliberately returns no row counts. Inventing "412 employees synced"
     * would put a number on screen that nothing produced, and a demo figure that
     * looks like data is the one kind of mock that outlives the mock.
     */
    syncFromIjp: (): Promise<void> => new Promise((resolve) => setTimeout(resolve, 2000)),
  },

  // ── Requisitions (API_SPEC §6 + FLOW2 F-4) ────────────────────────────────────
  requisitions: {
    /** Sortable via `sort_by`/`sort_order` (ListQuery); default created_at desc. */
    list: (
      filters: ListQuery & {
        flow_type?: FlowType
        status?: RequisitionStatus
        posting_entity_id?: string
        /** FLOW2 F-1 alias of posting_entity_id (subsidiary filter). */
        hiring_subsidiary_id?: string
        target_entity_id?: string
      } = {},
      options?: RequestOptions,
    ): Promise<PagedResponse<Requisition[]>> =>
      c.getPage<Requisition[]>(`/requisitions${qs(filters)}`, options),
    get: (id: string, options?: RequestOptions): Promise<Requisition> =>
      c.get<Requisition>(`/requisitions/${id}`, options),
    create: (input: CreateRequisitionInput, options?: RequestOptions): Promise<Requisition> =>
      c.post<Requisition>('/requisitions', input, options),
    update: (
      id: string,
      input: UpdateRequisitionInput,
      options?: RequestOptions,
    ): Promise<Requisition> => c.post<Requisition>(`/requisitions/${id}/update`, input, options),
    // FB-8: no `uploadJd`. `POST /requisitions/:id/upload-jd` was deleted with
    // the JD-document feature, so the method could only ever produce a 404.
    /**
     * POST /requisitions/:id/assign-recruiter (FLOW2 R4): set/clear the
     * assigned recruiter (employee_code) and/or the recruiter DL. At least one
     * field must be present; `assigned_recruiter_id: null` clears.
     */
    assignRecruiter: (
      id: string,
      input: AssignRecruiterInput,
      options?: RequestOptions,
    ): Promise<Requisition> =>
      c.post<Requisition>(`/requisitions/${id}/assign-recruiter`, input, options),
    publish: (
      id: string,
      input: PublishRequisitionInput = {},
      options?: RequestOptions,
    ): Promise<Requisition> => c.post<Requisition>(`/requisitions/${id}/publish`, input, options),
    // ── WAVE M6: stopping a posting ──────────────────────────────────────────
    //
    // CLOSE purges the pipeline; HOLD preserves it. For a close, run
    // `closurePreview` first and echo `will_reject.length` back as
    // `expected_active_count`: somebody can apply between the two calls, and a
    // mismatch is a 409 rather than an extra silent rejection. A hold skips
    // that handshake — there is no rejection for a late applicant to join.

    /** NON-MUTATING. Who is on this posting, bucketed as a CLOSE would treat them. */
    closurePreview: (id: string, options?: RequestOptions): Promise<ClosurePreview> =>
      c.get<ClosurePreview>(`/requisitions/${id}/closure-preview`, options),

    /**
     * OPEN | ON_HOLD → CLOSED, rejecting the active pipeline irreversibly. A
     * held posting closes DIRECTLY: the alternative — reopen, then close —
     * republishes the job to every eligible employee just to shut it down.
     */
    close: (
      id: string,
      input: StopRequisitionInput,
      options?: RequestOptions,
    ): Promise<ClosureResult> =>
      c.post<ClosureResult>(`/requisitions/${id}/close`, input, options),

    /**
     * OPEN → ON_HOLD. A REAL pause: it stops the posting taking NEW
     * applications and leaves the existing pipeline untouched, still workable,
     * and reversible via `reactivate`. Rejecting is CLOSE's job alone, so the
     * result comes back `rejected: 0` with the preserved count in `protected`.
     */
    hold: (
      id: string,
      input: StopRequisitionInput,
      options?: RequestOptions,
    ): Promise<ClosureResult> =>
      c.post<ClosureResult>(`/requisitions/${id}/hold`, input, options),

    /**
     * ON_HOLD | CLOSED → OPEN, on a FRESH visibility window (mandatory: the job
     * board checks the dates as well as the status, so reopening onto the old
     * window would leave the posting invisible). Does NOT un-reject anybody a
     * close rejected; a held posting never lost its pipeline in the first place.
     */
    reactivate: (
      id: string,
      input: ReactivateRequisitionInput,
      options?: RequestOptions,
    ): Promise<Requisition> =>
      c.post<Requisition>(`/requisitions/${id}/reactivate`, input, options),
  },

  // ── Jobs (employee-facing, API_SPEC §7 + FLOW2 F-5/F-6) ───────────────────────
  jobs: {
    /** `/jobs` supports broad `keyword` and explicit `search` + `searchBy`. */
    list: (
      filters: JobsListQuery = {},
      options?: RequestOptions,
    ): Promise<PagedResponse<Job[]>> => c.getPage<Job[]>(`/jobs${qs(filters)}`, options),
    /** `/jobs/all` carries the same filters and response contract as `/jobs`. */
    listAll: (
      filters: JobsListQuery = {},
      options?: RequestOptions,
    ): Promise<PagedResponse<Job[]>> => c.getPage<Job[]>(`/jobs/all${qs(filters)}`, options),
    /**
     * The `for_matching=true` LEAN variant of GET /jobs (FLOW2 §5): ids +
     * skills + meta for matching consumers — no eligibility stamping, no card
     * chrome.
     */
    listForMatching: (
      filters: ListQuery & {
        keyword?: string
        location?: string
        department?: string
        channel?: JobChannel
        subsidiary_id?: string
      } = {},
      options?: RequestOptions,
    ): Promise<PagedResponse<JobMatchingItem[]>> =>
      c.getPage<JobMatchingItem[]>(`/jobs${qs({ ...filters, for_matching: true })}`, options),
    get: (id: string, options?: RequestOptions): Promise<JobDetail> =>
      c.get<JobDetail>(`/jobs/${id}`, options),
    /** GET /jobs/:id/skill-payload — the §0.5 canonical skill payload (FLOW2 F-6). */
    skillPayload: (id: string, options?: RequestOptions): Promise<JobSkillPayload> =>
      c.get<JobSkillPayload>(`/jobs/${id}/skill-payload`, options),
    /** GET /jobs/skill-payloads?ids=a,b — batch §0.5 payloads (≤50 ids, FLOW2 F-6). */
    skillPayloads: (
      ids: string[],
      filters: { channel?: JobChannel } = {},
      options?: RequestOptions,
    ): Promise<JobSkillPayload[]> =>
      c.get<JobSkillPayload[]>(`/jobs/skill-payloads${qs({ ids: ids.join(','), ...filters })}`, options),
    /**
     * GET /jobs/match-scores?ids=a,b — ADVISORY skill-match percents for up to
     * 50 browse-visible jobs (MP-2, EMPLOYEE only). Employee skills = the
     * self-declared profile key_skills; an EMPTY basis returns
     * `{items: [], reason: 'NO_PROFILE_SKILLS'}`. Ids outside the browse
     * bounds are silently dropped; a gateway failure OMITS the affected items
     * (never a 5xx). Posture B (MP-5): display-only — the FE merges these onto
     * cards and may client-sort ONLY the home Recommended strip.
     */
    matchScores: (ids: string[], options?: RequestOptions): Promise<MatchScoresResult> =>
      c.get<MatchScoresResult>(`/jobs/match-scores${qs({ ids: ids.join(',') })}`, options),
    /**
     * GET /jobs/:id/similar — SR-1, roles LIKE THIS ONE. A JOB-to-JOB
     * neighbourhood: NOT personalised and NOT a match percent. The ranking is
     * the same for every viewer; only which candidates are visible (and each
     * card's `is_eligible`) is per-principal. `reason: 'NO_TARGET_SKILLS'` when
     * the posting being viewed names no skills. Display-only (Posture B).
     */
    similar: (
      id: string,
      limit?: number,
      options?: RequestOptions,
    ): Promise<SimilarRolesResult> =>
      c.get<SimilarRolesResult>(`/jobs/${id}/similar${qs({ limit })}`, options),
  },

  // ── Bookmarks (employee saved jobs) ─────────────────────────────────────────
  bookmarks: {
    /** GET /bookmarks — paginated list of the caller's bookmarked jobs. */
    list: (
      filters: ListQuery & { sort_order?: 'asc' | 'desc' } = {},
      options?: RequestOptions,
    ): Promise<PagedResponse<BookmarkedJobItem[]>> =>
      c.getPage<BookmarkedJobItem[]>(`/bookmarks${qs(filters)}`, options),
    /** POST /bookmarks — bookmark a job (idempotent). */
    add: (jobId: string, options?: RequestOptions): Promise<BookmarkMutation> =>
      c.post<BookmarkMutation>('/bookmarks', { job_id: jobId }, options),
    /** POST /bookmarks/remove — unbookmark a job (idempotent). */
    remove: (jobId: string, options?: RequestOptions): Promise<BookmarkMutation> =>
      c.post<BookmarkMutation>('/bookmarks/remove', { job_id: jobId }, options),
  },

  // ── Applications (API_SPEC §8 + FLOW2 F-7) ────────────────────────────────────
  applications: {
    /**
     * GET /applications/prefill?requisition_id= (FLOW2 F-7 — OWNER only):
     * seeds the apply form from current employee data and the profile summary.
     */
    getApplicationPrefill: (
      requisition_id: string,
      options?: RequestOptions,
    ): Promise<ApplicationPrefill> =>
      c.get<ApplicationPrefill>(`/applications/prefill${qs({ requisition_id })}`, options),
    /** An `Idempotency-Key` header may ride `options.headers` (replay-safe, TTL 24h). */
    submit: (
      input: SubmitApplicationInput,
      options?: RequestOptions,
    ): Promise<SubmitApplicationResult> =>
      c.post<SubmitApplicationResult>('/applications', input, options),
    /**
     * With `mine: true` (employee self view) the response also carries
     * `meta.summary = { active_count, closed_count }` (snake_case) — read it
     * off the returned `meta` for the ACTIVE|CLOSED toggle counts.
     */
    list: (
      filters: ListQuery & {
        requisition_id?: string
        status?: string
        /**
         * FB-10 "Current company" (STAFF only — ignored on the employee branch,
         * where every row is already the caller's own): the APPLICANT's entity.
         * One uuid, or a comma-separated list of up to 50. An EMPTY selection
         * must send NOTHING — never an empty string, which would read as a
         * filter matching nobody.
         */
        entity_id?: string
        /** FLOW2 F-7: force the OWN-rows branch (implied for employees). */
        mine?: boolean
        /** FLOW2 F-5/F-7: channel of the posting entity. */
        channel?: JobChannel
        /** FLOW2 F-7: catalog-driven grouping (ACTIVE = non-TERMINAL). */
        status_group?: ApplicationStatusGroup
      } = {},
      options?: RequestOptions,
    ): Promise<PagedResponse<Application[]>> =>
      c.getPage<Application[]>(`/applications${qs(filters)}`, options),
    get: (id: string, options?: RequestOptions): Promise<ApplicationDetail> =>
      c.get<ApplicationDetail>(`/applications/${id}`, options),
    /**
     * POST /applications/:id/withdraw — the APPLICANT'S OWN irreversible exit
     * (employee only). Empty body is a one-click withdraw; optional `message`
     * is stored as `closure_message`. A row the caller does not own is 404.
     */
    withdraw: (
      id: string,
      input: WithdrawApplicationInput = {},
      options?: RequestOptions,
    ): Promise<ApplicationDetail> =>
      c.post<ApplicationDetail>(`/applications/${id}/withdraw`, input, options),
    updateStatus: (
      id: string,
      input: UpdateApplicationStatusInput,
      options?: RequestOptions,
    ): Promise<Application> => c.post<Application>(`/applications/${id}/status`, input, options),
    /**
     * Merged-PDF export (recruiter/admin). The backend responds JSON:
     * `{ download_url, expires_at, count, format: 'pdf' }` — a short-lived
     * presigned GET URL (or a bare object ref in dev) + its ISO expiry (FLOW2
     * F-7). THIS is the endpoint's real contract; open `download_url` to fetch
     * the PDF. An `Idempotency-Key` header may ride `options.headers` (a
     * retried export replays the SAME download_url/expires_at).
     */
    export: (
      input: ExportApplicationsInput,
      options?: RequestOptions,
    ): Promise<ExportApplicationsResult> =>
      c.post<ExportApplicationsResult>('/applications/export', input, options),
    /**
     * Blob variant — ONLY for a future streaming deploy; the CURRENT backend
     * returns the JSON envelope above, so this throws. Portals should use
     * {@link export} + `download_url`.
     */
    exportBlob: (input: ExportApplicationsInput, options?: RequestOptions): Promise<Blob> =>
      c.postBlob('/applications/export', input, options),
  },

  // ── Status catalog (API_SPEC §8 — drives status-transition options) ───────────
  statusCatalog: {
    list: (options?: RequestOptions): Promise<StatusCatalogItem[]> =>
      c.get<StatusCatalogItem[]>('/status-catalog', options),

    // ── WAVE M6: the ADMIN half. HR_ADMIN-only. ─────────────────────────
    //
    // `current_status` is a plain string on the application with no foreign
    // key, so the server refuses the three edits that would strand live data —
    // renaming a referenced code, turning a status TERMINAL underneath people,
    // and removing the last INITIAL/TERMINAL. The UI reads the usage counts on
    // each row so it can grey those out instead of offering them and failing at
    // Save.
    adminList: (
      filters: ListQuery & { is_active?: 'all' | 'true' | 'false' } = {},
      options?: RequestOptions,
    ): Promise<PagedResponse<StatusCatalogAdminItem[]>> =>
      c.getPage<StatusCatalogAdminItem[]>(`/status-catalog/admin${qs(filters)}`, options),

    /** NON-MUTATING. Run before any edit that could strand live applications. */
    impactPreview: (
      input: StatusImpactPreviewInput,
      options?: RequestOptions,
    ): Promise<StatusImpactResult> =>
      c.post<StatusImpactResult>('/status-catalog/impact-preview', input, options),

    create: (
      input: CreateStatusCatalogInput,
      options?: RequestOptions,
    ): Promise<StatusCatalogAdminItem> =>
      c.post<StatusCatalogAdminItem>('/status-catalog', input, options),

    update: (
      input: UpdateStatusCatalogInput,
      options?: RequestOptions,
    ): Promise<StatusCatalogAdminItem> =>
      c.post<StatusCatalogAdminItem>('/status-catalog/update', input, options),

    remove: (id: string, options?: RequestOptions): Promise<{ ok: true }> =>
      c.post<{ ok: true }>('/status-catalog/delete', { id }, options),

    /** Whole-ladder renumber. EVERY status id, exactly once, in the new order. */
    reorder: (order: string[], options?: RequestOptions): Promise<{ renumbered: number }> =>
      c.post<{ renumbered: number }>('/status-catalog/reorder', { order }, options),
  },

  // ── Roles (Admin, API_SPEC §10) ───────────────────────────────────────────────
  roles: {
    list: (
      filters: ListQuery & { entity_id?: string; role?: Role; is_active?: boolean } = {},
      options?: RequestOptions,
    ): Promise<PagedResponse<RoleGrant[]>> =>
      c.getPage<RoleGrant[]>(`/roles${qs(filters)}`, options),
    grant: (input: GrantRoleInput, options?: RequestOptions): Promise<RoleGrant> =>
      c.post<RoleGrant>('/roles', input, options),
    revoke: (id: string, options?: RequestOptions): Promise<RoleGrant> =>
      c.post<RoleGrant>(`/roles/${id}/revoke`, undefined, options),
  },

  // ── Settings (Admin, API_SPEC §11) ────────────────────────────────────────────
  settings: {
    list: (options?: RequestOptions): Promise<Setting[]> =>
      c.get<Setting[]>('/admin/settings', options),
    update: (key: string, setting_value: string, options?: RequestOptions): Promise<Setting> =>
      c.post<Setting>(
        `/admin/settings/${encodeURIComponent(key)}/update`,
        { setting_value },
        options,
      ),
    /** Re-read the settings table into the singleton; returns the row count reloaded. */
    reload: (options?: RequestOptions): Promise<{ reloaded: number }> =>
      c.post<{ reloaded: number }>('/admin/settings/reload', undefined, options),
  },
  model: {
    status: (options?: RequestOptions): Promise<OllamaModelStatus> =>
      c.get<OllamaModelStatus>('/admin/model', options),
    check: (options?: RequestOptions): Promise<OllamaModelCheck> =>
      c.post<OllamaModelCheck>('/admin/model/check', undefined, options),
  },
} as const

export { qs as buildQueryString }

// --- flow2: employee surface additions ---

// ── MP-2: GET /jobs/match-scores (ADVISORY match percents, EMPLOYEE only) ───────

/** One ADVISORY match verdict (Posture B / MP-5 — display-only, never ordering). */
export interface MatchScoreItem {
  id: string
  /** 0–100 integer skill-match percent (advisory — never gates anything). */
  match_percent: number
  /** The employee-skill ∩ job-skill overlap the score is grounded in. */
  matched_skills: string[]
}

/**
 * The GET /jobs/match-scores payload (MP-2): `reason` is set ONLY when the
 * employee's skill basis (the self-declared profile key_skills) is EMPTY —
 * the backend never guesses skills from the roster. Unknown/foreign ids are
 * silently dropped; a gateway failure omits the affected items.
 */
export interface MatchScoresResult {
  items: MatchScoreItem[]
  reason?: 'NO_PROFILE_SKILLS'
}

/** SR-1 — one neighbour of the posting being viewed (`GET /jobs/:id/similar`). */
export interface SimilarRoleItem {
  /** The SAME summary card the board serves — rendered by the same component. */
  job: Job
  /** 0–100 posting-to-posting similarity. NOT the viewer's match percent. */
  similarity_percent: number
  /** Skills BOTH postings name; empty when only the semantic leg connected them. */
  shared_skills: string[]
}

export interface SimilarRolesResult {
  items: SimilarRoleItem[]
  /** The posting being viewed names no skills, so nothing could be compared. */
  reason?: 'NO_TARGET_SKILLS'
}

/**
 * MP-2 named helper — `GET /jobs/match-scores?ids=a,b` (≤50 ids, EMPLOYEE
 * only). Same contract as {@link mobilityApi.jobs.matchScores} (which it
 * delegates to): `{items, reason?}` with `reason: 'NO_PROFILE_SKILLS'` on an
 * empty skill basis. Advisory (Posture B): merge onto cards, never filter.
 */
export function getJobMatchScores(
  ids: string[],
  options?: RequestOptions,
): Promise<MatchScoresResult> {
  return mobilityApi.jobs.matchScores(ids, options)
}

// --- flow2: admin surface additions ---

// ── Wave GM-M: Template_GM exchange (export format + bulk imports) ──────────────

/**
 * The batch report of `POST /applications/import-outcomes` (backend
 * `outcomeImportReportSchema`) — the house upload-report shape WITHOUT a
 * `version` field (unlike {@link BulkUploadReport}).
 */
export interface OutcomeImportReport {
  total: number
  succeeded: number
  failed: number
  failures: RowFailure[]
}

/** One created row of the JOBS bulk import (`jobsImportReportSchema.created[]`). */
export interface JobsImportCreatedRow {
  row: number
  id: string
  /** Adopted (numeric JOB_ID ≤20 chars, unused) or generated job_code. */
  job_code: string
  /** `DRAFT` — or `CLOSED` when the sheet's JOB_CLOSED was truthy. */
  status: string
}

/**
 * The report of `POST /requisitions/import` (backend `jobsImportReportSchema`)
 * — the outcome-report shape plus the per-row `created` list.
 */
export interface JobsImportReport extends OutcomeImportReport {
  created: JobsImportCreatedRow[]
}

/**
 * Wave GM-M S2 — named export helper: `POST /applications/export` with an
 * explicit render format (`'pdf'` merged dossier | `'xlsx'` = the Template_GM
 * Application_DUMP 15-column workbook; default `'pdf'`). Same endpoint and
 * response contract as {@link mobilityApi.applications.export} — a JSON
 * envelope `{ download_url, expires_at, count, format }`; open `download_url`
 * to fetch the file. An `Idempotency-Key` header may ride `options.headers`
 * (a retried export replays the SAME download_url/expires_at).
 */
export function exportApplications(
  input: ExportApplicationsInput,
  format: ExportApplicationsFormat = 'pdf',
  options?: RequestOptions,
): Promise<ExportApplicationsResult> {
  return c.post<ExportApplicationsResult>(
    '/applications/export',
    { ...input, format: input.format ?? format },
    options,
  )
}

/**
 * Wave GM-M GM-4 — `POST /applications/import-outcomes` (multipart, field
 * `file`): the Template_GM APPLICATION_STATUS bulk outcome sheet (CSV or
 * .xlsx, content-sniffed server-side, ≤5MB). Each row rides the EXISTING
 * status pipeline (scope + catalog validator + CAS + notification);
 * `is_selected=1` → OFFERED (+ joining date), `0` → CLOSED with the comment
 * as the employee-facing message. Returns the standard batch report.
 */
export function importApplicationOutcomes(
  file: File,
  options?: RequestOptions,
): Promise<OutcomeImportReport> {
  const form = new FormData()
  form.append('file', file)
  return c.postMultipart<OutcomeImportReport>('/applications/import-outcomes', form, options)
}

/**
 * Wave GM-M GM-5 — `POST /requisitions/import` (multipart, field `file`,
 * HR_ADMIN): the Template_GM JOBS sheet (20 columns, CSV or .xlsx,
 * content-sniffed, ≤5MB) imported as DRAFT requisitions (CLOSED when
 * JOB_CLOSED is truthy). Duplicate job_code / already-imported JOB_ID rows
 * fail individually — never an overwrite; publish stays a human action.
 */
export function importRequisitions(
  file: File,
  options?: RequestOptions,
): Promise<JobsImportReport> {
  const form = new FormData()
  form.append('file', file)
  return c.postMultipart<JobsImportReport>('/requisitions/import', form, options)
}

// --- flow2: wave US additions ---

/**
 * Wave US (US-5/US-D4) — the `upload-jd` JD-generation verdict:
 * `true` = AI-composed structured sections + flattened description persisted;
 * `'fallback'` = the gateway was unavailable, the plain extracted text landed
 * in `job_description`; `false` = nothing generated (a non-empty description
 * is NEVER overwritten; no-text uploads generate nothing).
 */
export type JdGenerated = boolean | 'fallback'

/**
 * One `GET /requisitions/:id/history` row (US-3) — the append-only requisition
 * lifecycle audit trail, served NEWEST FIRST. `detail` carries NON-PII event
 * context (changed field names, visibility window, closure reason, …).
 */
export interface RequisitionHistoryItem {
  id: string
  requisition_id: string
  /** CREATED | UPDATED | PUBLISHED | CLOSED | RECRUITER_ASSIGNED | IMPORTED. */
  action: string
  actor_id: string
  detail: Record<string, unknown> | null
  created_at: string
}

/**
 * Wave US (US-3) — `GET /requisitions/:id/history`: the append-only lifecycle
 * trail, newest first. Access mirrors the US-D3 assignment scoping server-side
 * (HR_ADMIN or the assignee; entity RECRUITERs when unassigned) — a 403
 * simply hides the section client-side.
 */
export function getRequisitionHistory(
  id: string,
  options?: RequestOptions,
): Promise<RequisitionHistoryItem[]> {
  return c.get<RequisitionHistoryItem[]>(`/requisitions/${id}/history`, options)
}

/** The `GET /requisitions/:id/eligible-cohort` payload (US-1). */
export interface EligibleCohort {
  /** How many ACTIVE target-entity employees the eligibility engine admits today. */
  count: number
  /**
   * WAVE W3-WIRE (D2 mitigation) — the registry gate codes the ACTIVE match
   * policy has EXPLICITLY switched off, in registry order.
   *
   * This is the response where it matters most: the `count` IS the eligibility
   * engine's verdict summed over a roster, and publish is blocked at 0 — so a
   * gate that is off INFLATES the exact number a human signs off on, invisibly.
   * The backend always sends it (possibly `[]`); OPTIONAL here only so pre-W3
   * fixtures and a stale service stay assignable — an ABSENT field means "the
   * backend did not say", which is NOT the same as "nothing is disabled" and
   * must not be rendered as reassurance.
   */
  gates_disabled?: string[]
}

/**
 * Wave US (US-1, stories 2.2/2.14) — `GET /requisitions/:id/eligible-cohort`:
 * the publish-review preview ("N eligible employees will see this job"),
 * computed by the SAME engine that gates the employee board. Publish blocks
 * server-side (VALIDATION_ERROR) when this is 0 — the count is advisory
 * display, never a client-side gate.
 */
export function getEligibleCohort(
  id: string,
  options?: RequestOptions,
): Promise<EligibleCohort> {
  return c.get<EligibleCohort>(`/requisitions/${id}/eligible-cohort`, options)
}

// --- flow2: wave GM-C additions ---

/**
 * `POST /applications/share` body (Wave GM-C, M-4) — same scoping semantics as
 * {@link ExportApplicationsInput}: `application_ids` absent/empty ⇒ share ALL
 * applications of the requisition.
 */
export interface ShareApplicationsInput {
  requisition_id: string
  application_ids?: string[] | null
  /**
   * PER-SEND override of the To line (max 20, email-validated server-side).
   * Absent or empty ⇒ the backend resolves the recipients as it always has
   * (tagged subsidiary HR → posting entity HR SPOC → recruiter DL). Nothing is
   * stored: the next preview resolves the defaults again.
   */
  to?: string[] | null
  /** PER-SEND Cc line, same rules. Absent or empty ⇒ no Cc. */
  cc?: string[] | null
}

/**
 * The share receipt (backend `shareApplicationsResultSchema`): how many
 * profiles were emailed, the M-4 attachment packaging (`SINGLE` = the résumé
 * attached directly, `ZIP` = one archive of per-candidate résumés), and the
 * resolved recipients — the requisition's tagged subsidiary HR, else the
 * posting entity's HR SPOC, else the requisition's recruiter DL fallback
 * (organisational contact data, never applicant PII).
 */
export interface ShareApplicationsResult {
  count: number
  packaging: 'SINGLE' | 'ZIP'
  /**
   * WAVE GM-R (R3): `SUBSIDIARY_HR` = the requisition's tagged HR list, and it
   * is the COMMON case — this union omitted it, so the receipt's most frequent
   * value was not representable (matches the preview's own union).
   */
  recipient_kind: 'SUBSIDIARY_HR' | 'HR_SPOC' | 'RECRUITER_DL' | 'CUSTOM'
  /** The PRIMARY addressee (always the first of `recipients`). */
  recipient: string
  /** WAVE GM-R (R3): EVERY addressee. Optional — older payloads predate it. */
  recipients?: string[]
  /**
   * TRUE when the backend's [EMAIL_SWAP] adapter only SIMULATED the send (its
   * log-only fallback — no SMTP host configured). The mail did NOT leave the
   * server, so a surface must NOT report it as delivered. Optional: older
   * payloads predate the flag, and an absent value is treated as "real".
   */
  simulated?: boolean
}

/**
 * Wave GM-C (M-4) — `POST /applications/share`: emails the Application_DUMP
 * sheet + per-candidate generated résumés to the POSTING subsidiary's HR.
 * Wave GM-CV: the CV concept was removed group-wide — the GENERATED résumé is
 * the only per-candidate document the share carries.
 * Share IS the delivery: a mail failure surfaces as a 502-style
 * `SHARE_DELIVERY_FAILED` {@link ApiError} (no silent success, no
 * Idempotency-Key replay — a retry genuinely re-sends).
 */
export function shareApplications(
  input: ShareApplicationsInput,
  options?: RequestOptions,
): Promise<ShareApplicationsResult> {
  return c.post<ShareApplicationsResult>('/applications/share', input, options)
}

// --- flow2: FE-B operability additions (D2-G1 / GM-8 / D1-G6) ---

/** D1-G6: the `{ field, message }` error-details item, for dialog renderers. */
export type { ApiErrorDetail } from './api'

/**
 * Wave GM-M (GM-8) — the auto-close facets now ride STAFF list rows additively
 * (interface merge onto {@link Application}; absent on employee-facing lists
 * and on older payloads, hence optional). Lets the staff tables mark a cron
 * auto-closure without a per-row detail fetch.
 */
export interface Application {
  /** True when the auto-close cron closed the row (staff views only). */
  is_auto_closed?: boolean
  /** The auto-close instant (ISO-8601); null unless auto-closed. */
  auto_closed_at?: string | null
}

// --- flow2: GM-C share preview + SSO exchange additions ---

/**
 * One would-be attachment on the share preview (backend
 * `sharePreviewAttachmentSchema`) — EXACTLY what the send would attach.
 * `download_url` is a short-lived presigned GET (≤ 900s, M-4; a bare object
 * ref on the unsigned dev store) — link it directly, never store it.
 */
export interface SharePreviewAttachment {
  filename: string
  content_type: string
  size_bytes: number
  download_url: string
}

/**
 * The `POST /applications/share/preview` payload (backend
 * `sharePreviewResultSchema`): the rendered mail the share WOULD transmit —
 * resolved recipient, subject, plain-text body, and the attachment set behind
 * presigned downloads. NOTE the preview's `packaging` is lowercase
 * (`'single' | 'zip'`), unlike the send receipt's uppercase enum.
 */
export interface SharePreviewResult {
  /** The PRIMARY addressee (wire compatibility — always the first of `recipients`). */
  recipient: string
  /**
   * WAVE GM-R (R3): EVERY addressee the real send would use (> 1 only when the
   * requisition tags a subsidiary-HR list). Optional here because older
   * payloads predate it — render `recipients ?? [recipient]`.
   */
  recipients?: string[]
  /** WAVE GM-R (R3): SUBSIDIARY_HR = the requisition's tagged HR list (preferred). */
  recipient_kind: 'SUBSIDIARY_HR' | 'HR_SPOC' | 'RECRUITER_DL' | 'CUSTOM'
  /** The Cc line the send would use — the caller's per-send override, if any. */
  cc?: string[]
  subject: string
  /** The rendered plain-text body the send would transmit. */
  body: string
  packaging: 'single' | 'zip'
  count: number
  /** ISO instant the presigned `attachments[].download_url`s stop working. */
  expires_at: string
  attachments: SharePreviewAttachment[]
}

/**
 * Wave GM-C — `POST /applications/share/preview`: the EXACT share pipeline
 * WITHOUT the delivery (no mail, no SHARED history; audit format 'preview',
 * US-4). Same body semantics as {@link shareApplications}: `application_ids`
 * absent/empty ⇒ preview ALL applications of the requisition. A missing
 * recipient / empty scope throws a 400 VALIDATION_ERROR {@link ApiError} —
 * the send would fail the same way, so callers should keep Send gated on it.
 */
export function previewShare(
  input: ShareApplicationsInput,
  options?: RequestOptions,
): Promise<SharePreviewResult> {
  return c.post<SharePreviewResult>('/applications/share/preview', input, options)
}

/**
 * The `POST /auth/sso/exchange` payload — the SAME shape as /auth/otp/verify
 * (`OtpVerifyResult` in lib/mobility-auth.ts) BY BACKEND CONTRACT, so the FE
 * reuses its token handling verbatim.
 */
export interface SsoExchangeResult {
  token: string
  roster_id: string
  entity_id: string
  employee_code: string
}

/**
 * SSO bootstrap — `POST /auth/sso/exchange {token}`: swap the bank IdP's
 * external JWT for a minted mobility session token. Anonymous by design (no
 * session exists yet). Failures: 401 (bad/expired/replayed external token, or
 * not found in the employee master — one generic message), 503 (SSO not
 * configured). NEVER-LOG-TOKENS: callers must not log or persist the external
 * token; ONLY the returned mobility `token` is stored (lib/mobility-auth.ts
 * `signInWithSso` does that, mirroring OTP verify).
 */
export function exchangeSsoToken(
  token: string,
  options?: RequestOptions,
): Promise<SsoExchangeResult> {
  return c.post<SsoExchangeResult>('/auth/sso/exchange', { token }, options)
}

// --- flow2: in-process recommender additions (MATCH_SERVICE_PROPOSAL DECISION) ---

/**
 * One ranked job recommendation (`GET /recommendations/jobs`): the EXISTING
 * board card DTO (same visibility scope + eligibility stamping as GET /jobs)
 * plus the shared cached match verdict (MP-3). ADVISORY (Posture B, D-2/MP-5):
 * display ordering only — a score never gates eligibility, board visibility,
 * or a status.
 */
export interface JobRecommendationItem {
  job: Job
  /** 0–100 integer skill-match percent (advisory — never gates anything). */
  match_percent: number
  /** The employee-skill ∩ job-skill overlap the rank is grounded in. */
  matched_skills: string[]
  /**
   * The per-scorer arithmetic BEHIND `match_percent` (Wave B2 / D1) — keyed by
   * scorer code (`skill_overlap`, `semantic`, `location_fit`, `grade_proximity`).
   *
   * This is what makes the number advisory rather than folklore: a recruiter can
   * see that 78% is 0.40 of skill overlap plus 0.30 of semantic similarity and
   * so on. `contribution` values sum to `match_percent / 100`. A scorer that
   * could not compute is ABSENT, not zero — the blend re-normalizes over the
   * ones that did, so a missing signal never silently penalises a job.
   */
  score_breakdown?: Record<string, { raw: number; weight: number; contribution: number }>
}

/**
 * The jobs-recs payload, served ALREADY RANKED (desc by match_percent; ties:
 * newer visibility_start first) — the FE renders payload order verbatim and
 * never re-sorts. `reason` is set ONLY on an empty profile-skill basis (MP-2 —
 * the nudge contract). Jobs the gateway could not score are OMITTED (they stay
 * fully visible on the board — nothing is gated).
 */
export interface JobRecommendationsResult {
  items: JobRecommendationItem[]
  reason?: 'NO_PROFILE_SKILLS'
  /**
   * Registry gates the ACTIVE policy has EXPLICITLY switched off, in evaluation
   * order (the D2 mitigation). NON-EMPTY means a config row is changing WHO MAY
   * APPLY, and the UI is expected to say so rather than bury it.
   */
  gates_disabled?: string[]
  /**
   * HOW THE CANDIDATE POOL WAS BUILT (additive, advisory).
   *
   * `score_breakdown` on each item answers "why did this job score 62 %".
   * This answers the other question — "why was this job a candidate at all, and
   * where is the one I expected?" — which scoring cannot, because ranking
   * happens in Postgres and POOLING happens in the search index.
   */
  retrieval?: {
    /** MEILI = the index proposed the pool; DATABASE = it could not, so the board did. */
    source: 'MEILI' | 'DATABASE'
    /** HYBRID (vector+text) · KEYWORD (text only) · FILTERS (no query text). */
    leg: string | null
    /** Ids proposed BEFORE the eligibility gates removed any. */
    candidates: number | null
    /** Engine filter expressions, verbatim — a debugging artefact, not display copy. */
    filters: string[]
    /**
     * The SAME narrowing in words, built server-side from the same input the
     * predicates came from (`explainJobFilters`). This is what the screen
     * renders: `visibility_start <= 1786614720` is true and unreadable, and a
     * panel whose entire job is to be read should not print an epoch at anyone.
     * Optional for wire compatibility with a service that predates it.
     */
    filters_explained?: Array<{ label: string; detail: string }>
    /** Whether the employee's skill centroid was available to rank with. */
    used_employee_vector: boolean
    took_ms: number | null
    /** Why it fell back, when it did — never inferred from a zero count. */
    fallback_reason?: 'RETRIEVAL_DISABLED' | 'INDEX_UNAVAILABLE'
  }
}

/** Filters that narrow the advisory recommendation candidate pool before scoring. */
export interface JobRecommendationFilters {
  location?: string
  locations?: string[]
  department?: string
  subsidiary_id?: string
  posting_entity_ids?: string[]
  search?: string
  searchBy?: 'job_title' | 'job_id'
  page?: number
}

/**
 * `GET /recommendations/jobs?limit=` (EMPLOYEE only; limit defaults to 10
 * server-side, hard-capped at 25). The candidate pool is the caller's OWN
 * board (job_visibility_mode + eligibility stamping inherited from GET /jobs).
 * Posture B: the ranked payload drives the home strip's DISPLAY order only —
 * the /jobs board order is never touched.
 */
export function getJobRecommendations(
  limit?: number,
  options?: RequestOptions,
  filters: JobRecommendationFilters = {},
): Promise<JobRecommendationsResult> {
  return c.get<JobRecommendationsResult>(`/recommendations/jobs${qs({ limit, ...filters })}`, options)
}

/** Paging-aware recommendations read; carries the board facets in `meta`. */
export function getJobRecommendationsPage(
  limit: number | undefined,
  filters: JobRecommendationFilters = {},
  options?: RequestOptions,
): Promise<PagedResponse<JobRecommendationsResult>> {
  return c.getPage<JobRecommendationsResult>(`/recommendations/jobs${qs({ limit, ...filters })}`, options)
}

/**
 * One ranked applicant (`GET /recommendations/applicants` — ADVISORY, display
 * ordering only). `match_percent` = the frozen MP-4 apply-time stamp when
 * present (Decimal-backed — may be fractional), else a TRANSIENT recompute
 * (never persisted); null = uncomputable, ranked last but ALWAYS listed.
 * `matched_skills` is empty for stamped rows (the stamp freezes the score only).
 */
export interface ApplicantRankingItem {
  application_id: string
  employee_code: string
  match_percent: number | null
  matched_skills: string[]
  /** MP-4: 'SCORED' | 'UNAVAILABLE' | null (pre-MP rows). Advisory. */
  skill_match_status: string | null
  current_status: string
}

/** The applicants-ranking payload (ranked desc; null scores last, always listed). */
export interface ApplicantRankingResult {
  items: ApplicantRankingItem[]
}

/**
 * `GET /recommendations/applicants?requisition_id=` (staff — the widened
 * RECRUITER/HR_ADMIN/HR_PARTNER set; the backend re-derives the real
 * per-requisition entitlement via the shared US-D3 predicate). POSTURE B
 * (D-2/MP-5): the ranking is a DISPLAY ordering for the applications table's
 * opt-in 'Rank by match' toggle — a named human decides every advance/reject;
 * nothing here moves a status.
 *
 * WAVE W3-WIRE: PAGINATED (`page` default 1, `limit` default 20 / max 100), in
 * the applications-list dialect — so this returns `{data, meta}` and the FULL
 * ranked count rides `meta.pagination.totalItems`. The ranking is computed over
 * the WHOLE applicant set and only then sliced, so page 2 continues page 1 and
 * uncomputable rows sort last GLOBALLY, not last-within-page.
 *
 * Callers that need ranks for an arbitrary set of application ids (the
 * applications table re-orders the page it is already showing) must NOT assume
 * one unbounded list — use {@link getApplicantRankingAll}.
 */
export function getApplicantRanking(
  requisitionId: string,
  params: { page?: number; limit?: number } = {},
  options?: RequestOptions,
): Promise<PagedResponse<ApplicantRankingResult>> {
  return c.getPage<ApplicantRankingResult>(
    `/recommendations/applicants${qs({ requisition_id: requisitionId, ...params })}`,
    options,
  )
}

/**
 * The BOUND on any client-side page walk: 100 rows/request (the backend's max)
 * × 10 requests. A walk exists because a caller needs the ranks of rows it
 * ALREADY has — never to render 1,000 rows — so the bound is a guard against a
 * pathological requisition, not a page size.
 */
const RANKING_WALK_LIMIT = 100
const RANKING_WALK_MAX_PAGES = 10

/** The whole ranking, walked page by page (see {@link getApplicantRankingAll}). */
export interface ApplicantRankingAll {
  /** Ranked rows in server order — a PREFIX of the ranking when `truncated`. */
  items: ApplicantRankingItem[]
  /** D2: the gate codes the ACTIVE policy switched off (from the first page). */
  gates_disabled: string[]
  /** The FULL ranked count the server reports, even when `items` is shorter. */
  total: number
  /** True when the walk stopped at the bound — ranks below it are UNKNOWN. */
  truncated: boolean
}

/**
 * Walk `GET /recommendations/applicants` to the end (bounded) and return the
 * whole ranking as one list.
 *
 * WHY THIS EXISTS: the applications table pages on its OWN order (newest
 * first). To re-order the page it is showing it needs the rank of THOSE
 * application ids, which can sit anywhere in the ranking — so requesting "the
 * ranking's page N" would rank a different set of people and silently sink
 * whatever it missed. Taking only page 1 would do the same to anyone outside
 * the top 20. The walk is bounded and REPORTS `truncated` rather than pretending
 * an unranked row scored badly.
 *
 * A backend that sends no `meta.pagination` (pre-W3) answers in ONE request —
 * the walk stops after the first page.
 */
export async function getApplicantRankingAll(
  requisitionId: string,
  options?: RequestOptions,
): Promise<ApplicantRankingAll> {
  const items: ApplicantRankingItem[] = []
  let gatesDisabled: string[] = []
  let total = 0
  let truncated = false

  for (let page = 1; page <= RANKING_WALK_MAX_PAGES; page += 1) {
    const { data, meta } = await getApplicantRanking(
      requisitionId,
      { page, limit: RANKING_WALK_LIMIT },
      options,
    )
    items.push(...data.items)
    if (page === 1) gatesDisabled = data.gates_disabled ?? []
    const pagination = meta.pagination
    total = pagination?.totalItems ?? items.length
    if (!pagination?.hasNext) break
    if (page === RANKING_WALK_MAX_PAGES) truncated = true
  }

  return { items, gates_disabled: gatesDisabled, total, truncated }
}

// --- flow2: wave GM-R additions (R1/R2/R3 — optional target, recommender-owned skills) ---

/**
 * The outcome of an on-demand skill re-extraction (Wave GM-R, R2). ADVISORY,
 * like everything else the recommender emits: the rows it appends are
 * suggestions attached to a posting, never a decision about anyone.
 *
 * `status` is one of:
 *  • `EXTRACTED` — the gateway answered (`inserted` may be 0 on an idempotent re-run);
 *  • `NO_JD_TEXT` — nothing to read yet (no gateway call was made);
 *  • `DEGRADED` — the gateway failed / returned nothing usable. NOTHING was
 *    persisted, and deliberately NOT an error: a degraded AI tier never 5xxs.
 */
export interface ExtractSkillsResult {
  requisition_id: string
  status: 'EXTRACTED' | 'NO_JD_TEXT' | 'DEGRADED' | string
  /** Skill candidates the gateway returned. */
  candidates: number
  /** Rows actually APPENDED (an existing skill is never duplicated). */
  inserted: number
  /** Candidates skipped as already present / not usable. */
  skipped: number
  canonicalized: number
  /** Previously-retired skills re-admitted this run; 0 unless `force` asked for it. */
  overridden_removals: number
  canonicalization: string
  /** The gateway inference-artifact id backing this run (null when degraded). */
  advisory_ref: string | null
}

/**
 * `POST /recommendations/requisitions/:id/extract-skills` (Wave GM-R R2 —
 * HR_ADMIN, or the requisition's ASSIGNED recruiter): re-read the
 * posting's job-description text and APPEND extracted skills to
 * `tmext_mobility_requisition_skills`. IDEMPOTENT (no duplicates; a MANUAL or
 * IMPORTED row is never touched) and QUIETLY DEGRADING (a gateway failure
 * resolves with `status: 'DEGRADED'`, not a rejection).
 *
 * Skills are NOT authorable anywhere in the UI — this is the only way a human
 * influences them, and even then only by asking the recommender to look again.
 *
 * `force` (default false): also replaces the standing AI answer and
 * re-admits skills a human previously retired on THIS posting. Unforced
 * re-extraction only adds, which can never fill a posting whose skills were
 * all retired — `force` is the deliberate "start over" for that one case.
 */
export function extractRequisitionSkills(
  requisitionId: string,
  force = false,
  options?: RequestOptions,
): Promise<ExtractSkillsResult> {
  return c.post<ExtractSkillsResult>(
    `/recommendations/requisitions/${requisitionId}/extract-skills`,
    { force },
    options,
  )
}

// --- wave W2: the HR REVIEW surface for requisition skills --------------------

/**
 * WAVE W2 — ONE stored skill ROW, as the review surface sees it.
 *
 * The requisition DTO carries skill NAMES, which is enough to DISPLAY a posting
 * and not enough to REVIEW one: a reviewer cannot tell a curated requirement
 * from a keyword the JD upload's heuristic lifted out of boilerplate without
 * knowing where the row came from. That is what this shape adds.
 *
 * `id` is the ROW id — the value {@link removeRequisitionSkills} takes. It is
 * NOT `skill_id` (the canonical SKILL_MASTER id); deleting by the wrong one
 * deletes the wrong thing.
 */
export interface RequisitionSkillRow {
  /** The ROW id (what the delete endpoint takes) — NOT the canonical skill_id. */
  id: string
  skill: string
  /** SKILL | CATEGORY | SIGNATURE. */
  skill_type: string
  /** AI_EXTRACTED | MANUAL | IMPORTED — the provenance a reviewer judges. */
  source: string
  /** Advisory 0–1 where one exists; null for curated and keyword-heuristic rows. */
  confidence: number | null
  /** The canonical Thrive SKILL_MASTER id when the row resolved to one. */
  skill_id: string
  can_restore: boolean
  restore_block_reason: string | null
  /** Which producer wrote it — `JD_HEURISTIC` for upload-jd rows; null pre-W2. */
  producer: string | null
  /** The JD phrase behind the row, when recorded — the evidence for the chip. */
  source_phrase: string | null
  /** Structured JD evidence retained for Admin review and extraction tuning. */
  section: string | null
  section_weight: number | null
  similarity: number | null
  evidence_score: number | null
  supporting_phrases: number | null
  absorbed_skill_ids: string[]
  extracted_at: string | null
  created_at: string | null
  /** True for a TOMBSTONE (only ever present with `include_removed=true`). */
  is_removed: boolean
}

/** The `GET /recommendations/requisitions/:id/skills` payload (paginated). */
export interface RequisitionSkillsPage {
  requisition_id: string
  items: RequisitionSkillRow[]
}

/**
 * `GET /recommendations/requisitions/:id/skills` (Wave W2 — HR_ADMIN, or
 * the requisition's ASSIGNED recruiter; the SAME gate as attaching a skill,
 * because a principal who can add one must be able to take it back).
 *
 * PAGINATED (page/limit, limit max 100). `include_removed=true` also lists the
 * TOMBSTONES, so a removal is visible and can be undone.
 */
export function listRequisitionSkills(
  requisitionId: string,
  params: { page?: number; limit?: number; include_removed?: boolean } = {},
  options?: RequestOptions,
): Promise<PagedResponse<RequisitionSkillsPage>> {
  return c.getPage<RequisitionSkillsPage>(
    `/recommendations/requisitions/${requisitionId}/skills${qs(params)}`,
    options,
  )
}

/** The whole stored skill set for one requisition, walked page by page. */
export interface RequisitionSkillsAll {
  items: RequisitionSkillRow[]
  total: number
  /** True when the walk stopped at the bound (see {@link listAllRequisitionSkills}). */
  truncated: boolean
}

/**
 * Walk {@link listRequisitionSkills} to the end (bounded by the same
 * 100 × 10 guard as the ranking walk) — the review panel judges a posting's
 * WHOLE skill set, and a skill silently below the page fold is exactly the
 * unreviewed row this surface exists to eliminate.
 *
 * A backend that sends no `meta.pagination` answers in ONE request.
 */
export async function listAllRequisitionSkills(
  requisitionId: string,
  params: { include_removed?: boolean } = {},
  options?: RequestOptions,
): Promise<RequisitionSkillsAll> {
  const items: RequisitionSkillRow[] = []
  let total = 0
  let truncated = false

  for (let page = 1; page <= RANKING_WALK_MAX_PAGES; page += 1) {
    const { data, meta } = await listRequisitionSkills(
      requisitionId,
      { ...params, page, limit: RANKING_WALK_LIMIT },
      options,
    )
    items.push(...data.items)
    const pagination = meta.pagination
    total = pagination?.totalItems ?? items.length
    if (!pagination?.hasNext) break
    if (page === RANKING_WALK_MAX_PAGES) truncated = true
  }

  return { items, total, truncated }
}

/** Per-row outcome of a removal — the batch NEVER fails as a whole. */
export interface RemoveRequisitionSkillsItem {
  id: string
  /** The removed row's skill text; null when the id resolved to nothing. */
  skill: string | null
  /** REMOVED | ALREADY_REMOVED | NOT_FOUND | FAILED. */
  status: string
}

export interface RemoveRequisitionSkillsResult {
  requisition_id: string
  requested: number
  removed: number
  items: RemoveRequisitionSkillsItem[]
}

/**
 * `POST /recommendations/requisitions/:id/skills/delete` (Wave W2) — retire
 * skill ROWS a human has judged wrong before they score.
 *
 * SOFT-DELETE: the row survives as a TOMBSTONE, and that tombstone is what
 * stops the next JD upload or extraction sweep putting the rejected skill
 * straight back (the partial unique index covers non-deleted rows only and
 * cannot do that on its own) — without it, review would be theatre.
 *
 * IDEMPOTENT and per-row isolated: a repeated id answers `ALREADY_REMOVED`, an
 * unknown id `NOT_FOUND`, and neither fails the batch — a double-clicked chip
 * must not 404 the screen. Takes ROW ids, never canonical `skill_id`s.
 *
 * ADVISORY (Posture B): removal re-orders recommendations. It never moves an
 * application and never changes an eligibility verdict.
 */
export function removeRequisitionSkills(
  requisitionId: string,
  skillRowIds: string[],
  options?: RequestOptions,
): Promise<RemoveRequisitionSkillsResult> {
  return c.post<RemoveRequisitionSkillsResult>(
    `/recommendations/requisitions/${requisitionId}/skills/delete`,
    { skill_row_ids: skillRowIds },
    options,
  )
}

export interface AttachRequisitionSkillsResult {
  requisition_id: string
  requested: number
  attached: number
  items: { skill_id: string; skill: string; status: 'ATTACHED' }[]
}

/**
 * `POST /recommendations/requisitions/:id/skills/attach` — attach CURATED
 * skills, which land as `source: MANUAL`.
 *
 * This is also the UNDO for {@link removeRequisitionSkills}: curation
 * deliberately IGNORES tombstones (the "MANUAL/IMPORTED are untouchable" rule
 * binds the AI producers, not the human), so re-attaching a removed skill
 * restores it — as a MANUAL row, which is the honest provenance for it.
 * IDEMPOTENT: re-posting the same payload attaches 0 and skips the rest.
 */
export function attachRequisitionSkills(
  requisitionId: string,
  skillIds: string[],
  options?: RequestOptions,
): Promise<AttachRequisitionSkillsResult> {
  return c.post<AttachRequisitionSkillsResult>(
    `/recommendations/requisitions/${requisitionId}/skills/attach`,
    { skill_ids: skillIds },
    options,
  )
}

// --- flow2: wave GM-P additions (the deterministic recommender — D1 / D2) ------

/**
 * ONE scorer's contribution to a match percent (API_SPEC "The `score_breakdown`
 * object"). `raw` is the scorer's own 0..1 answer, `weight` its weight under
 * the ACTIVE policy, `contribution` = `raw × weight` — its share of the
 * numerator BEFORE re-normalization.
 */
export interface ScoreComponent {
  raw: number
  weight: number
  contribution: number
}

/**
 * D1 — the explainable replacement for the opaque LLM percentage: scorer code →
 * its component. A scorer that COULD NOT COMPUTE is ABSENT from the map, and
 * that absence is itself the explanation ("we had no location signal", "no
 * vectors were available"). Rendering an absent leg as 0 would be a lie: the
 * blend re-normalizes over the legs that answered, it does not score the
 * silent ones zero. Every surface must read an absent code as NOT APPLICABLE.
 *
 * `match_percent = round(100 × Σ(raw × weight) / Σ(weight over non-null legs))`;
 * all-null ⇒ `match_percent: null` with an EMPTY breakdown.
 */
export type ScoreBreakdown = Record<string, ScoreComponent>

/**
 * The scorer registry, in DISPLAY order (mirrors the backend's pinned
 * `SCORER_CODES`). The UI iterates THIS list so every registry leg is named
 * even when the payload omits it — plus any unknown code the payload carries,
 * because scorers are a registry and a new one must not need a FE release.
 *
 * FB-5 removed `keyword`. It is NOT kept as a courtesy entry: the admin console
 * seeds its weight inputs from these codes and POSTs every one of them, and the
 * backend write schema 400s on a code no registry implements — so a stale entry
 * here is not cosmetic, it is a console that cannot save.
 */
export const SCORER_CODES = [
  'skill_overlap',
  'semantic',
  'location_fit',
  'grade_proximity',
  'work_mode_fit',
] as const
export type ScorerCode = (typeof SCORER_CODES)[number]

/**
 * The gate registry, in EVALUATION order (mirrors the backend's pinned
 * `GATE_CODES`). GATES DECIDE ELIGIBILITY — scorers only order. The admin
 * console renders toggles from the API's `registry.gates` (the backend always
 * sends it) and falls back to this list only for a pre-FB-5 service.
 *
 * FB-5 removed `keyword_must` — see the note on {@link SCORER_CODES} for why a
 * removed code must leave this list rather than linger.
 */
export const GATE_CODES = [
  'job_open',
  'entity_scope',
  'guardrail_pregate',
  'eligibility_allowlist',
  'grade_equivalence',
  'department_lock',
  'location_lock',
  'function_lock',
  'grade_facet',
] as const
export type GateCode = (typeof GATE_CODES)[number]

/** The policy stamp some recommender payloads carry (which version scored this). */
export interface MatchPolicyStamp {
  policy_code: string
  version: number
}

/**
 * ADDITIVE (wave GM-P): `score_breakdown` rides every surface that returns a
 * `match_percent`. Optional here because a pre-GM-P backend simply omits it —
 * the badge/ring keeps working, only the explanation is missing.
 */
export interface MatchScoreItem {
  score_breakdown?: ScoreBreakdown
}

export interface JobRecommendationItem {
  score_breakdown?: ScoreBreakdown
  ranking_profile?: { source: 'DEFAULT' | 'REQUISITION'; version: number | null }
}

/**
 * D2 mitigation on the wire: `gates_disabled` names the gate codes the ACTIVE
 * policy has EXPLICITLY switched off, in registry order. FB-5 removed
 * `keyword_must` — the one gate the seeded default used to ship OFF — so the
 * default is now `[]` and ANYTHING in this array means a config row has
 * weakened a gate that decides who may apply.
 */
export interface JobRecommendationsResult {
  gates_disabled?: string[]
  policy?: MatchPolicyStamp
  /** Stable numeric feature contract used by offline evaluation / future LTR. */
  feature_schema_version?: string
}

export interface ApplicantRankingItem {
  /**
   * NULLABLE by contract: a row served from the frozen MP-4 apply-time stamp
   * has NO breakdown (the stamp persists a score, not its components), and
   * inventing one would misrepresent how that number was produced.
   */
  score_breakdown?: ScoreBreakdown | null
}

export interface ApplicantRankingResult {
  gates_disabled?: string[]
  policy?: MatchPolicyStamp
}

// ── Admin: the match policy (GET/POST /admin/match-policy) ─────────────────────

/** One gate's configuration under a policy version. */
export interface GateConfigDTO {
  enabled: boolean
  params?: Record<string, unknown> | null
}

/** The scorer block: weights per scorer code + the display floor. */
export interface ScorersConfigDTO {
  weights: Record<string, number>
  /** A DISPLAY floor only — never a gate (Posture B). */
  min_total_score: number
}

/** One stored policy version (the JSONB row, snake_case on the wire). */
export interface MatchPolicyVersion {
  policy_code: string
  version: number
  is_active: boolean
  gates: Record<string, GateConfigDTO>
  scorers: ScorersConfigDTO
  effective_from: string | null
  effective_to: string | null
  description?: string | null
  created_at?: string
  created_by?: string | null
}

/**
 * The CODE-SIDE registries. The console renders its toggles/inputs from these,
 * so adding a gate or a scorer backend-side needs no frontend release.
 */
export interface MatchPolicyRegistry {
  gates: string[]
  scorers: string[]
}

/**
 * One append-only `tmext_mobility_match_policy_history` row — the D2 audit
 * mitigation: no policy change without actor + timestamp + before/after diff.
 */
export interface MatchPolicyHistoryItem {
  id?: string
  policy_code?: string
  version: number
  /** CREATED | UPDATED | ACTIVATED | DEACTIVATED. */
  action: string
  actor_id: string
  gates_before: Record<string, unknown> | null
  gates_after: Record<string, unknown> | null
  scorers_before: Record<string, unknown> | null
  scorers_after: Record<string, unknown> | null
  created_at: string
}

/** The `GET /admin/match-policy` payload. */
export interface MatchPolicyView {
  /** null only on a backend with no active row AND no built-in fallback. */
  active: MatchPolicyVersion | null
  /** D2: the gates this policy switched OFF, in registry order. */
  gates_disabled: string[]
  registry?: MatchPolicyRegistry
  /** Present when `include_history=true`; paged via `meta.pagination`. */
  history?: MatchPolicyHistoryItem[]
}

/** The `POST /admin/match-policy` body — a PARTIAL patch over the active version. */
export interface SaveMatchPolicyInput {
  policy_code?: string
  gates?: Record<string, GateConfigDTO>
  scorers?: { weights?: Record<string, number>; min_total_score?: number }
  /** Default false — write the version, leave it inactive. */
  activate?: boolean
  effective_from?: string | null
  effective_to?: string | null
  /** Internal rationale, stored on the row and carried into the audit trail. */
  description?: string | null
}

/**
 * The write receipt. `gates_disabled` is returned so the caller sees IMMEDIATELY
 * whether the write weakened access — the UI must name these gates back to the
 * admin rather than reporting a bare "saved".
 */
export interface SaveMatchPolicyResult {
  policy_code: string
  version: number
  is_active: boolean
  gates_disabled: string[]
}

/**
 * `GET /admin/match-policy` (HR_ADMIN) — the active policy, the code-side
 * registries, and (with `include_history`) the append-only version history.
 * Returns `{data, meta}` so the history's `meta.pagination` is available.
 */
export function getMatchPolicy(
  params: {
    policy_code?: string
    include_history?: boolean
    page?: number
    limit?: number
  } = {},
  options?: RequestOptions,
): Promise<PagedResponse<MatchPolicyView>> {
  return c.getPage<MatchPolicyView>(`/admin/match-policy${qs(params)}`, options)
}

/**
 * `POST /admin/match-policy` (HR_ADMIN) — append a NEW policy version
 * (never a mutation of an existing one) and optionally activate it.
 *
 * D2 (accepted risk, knowingly chosen): this endpoint can DISABLE a gate that
 * decides who may apply. The mitigations are append-only history with actor +
 * diff, HR_ADMIN-only access, and the {@link SaveMatchPolicyResult.gates_disabled}
 * flag the console must surface back to the human who saved.
 */
export function saveMatchPolicy(
  input: SaveMatchPolicyInput,
  options?: RequestOptions,
): Promise<SaveMatchPolicyResult> {
  return c.post<SaveMatchPolicyResult>('/admin/match-policy', input, options)
}

// ── Admin: per-requisition advisory ranking weights ─────────────────────────

export interface RequisitionRankProfileHistoryItem {
  id: string
  version: number
  action: 'CREATED' | 'UPDATED' | 'RESET'
  actor_id: string
  weights_before: Record<string, number> | null
  weights_after: Record<string, number> | null
  rationale: string
  created_at: string
}

export interface RequisitionRankProfileView {
  requisition_id: string
  source: 'DEFAULT' | 'REQUISITION'
  /** Concurrency token for the compact row, including after a reset. */
  record_version: number | null
  profile_version: number | null
  weights: Record<string, number>
  rationale: string | null
  updated_at: string | null
  updated_by: string | null
  history: RequisitionRankProfileHistoryItem[]
}

export function getRequisitionRankProfile(
  requisitionId: string,
  options?: RequestOptions,
): Promise<RequisitionRankProfileView> {
  return c.get<RequisitionRankProfileView>(
    `/admin/requisitions/${requisitionId}/rank-profile`,
    options,
  )
}

export function saveRequisitionRankProfile(
  requisitionId: string,
  weights: Record<string, number>,
  rationale: string,
  expectedVersion: number | null,
  options?: RequestOptions,
): Promise<RequisitionRankProfileView> {
  return c.post<RequisitionRankProfileView>(
    `/admin/requisitions/${requisitionId}/rank-profile`,
    { weights, rationale, expected_version: expectedVersion },
    options,
  )
}

export function resetRequisitionRankProfile(
  requisitionId: string,
  rationale: string,
  expectedVersion: number | null,
  options?: RequestOptions,
): Promise<RequisitionRankProfileView> {
  return c.post<RequisitionRankProfileView>(
    `/admin/requisitions/${requisitionId}/rank-profile/reset`,
    { rationale, expected_version: expectedVersion },
    options,
  )
}

// ── Admin: shadow-mode role-archetype classification (direction change, 2026-08-18) ──

export type RoleArchetype = 'TECHNICAL' | 'RELATIONSHIP_FACING' | 'LEADERSHIP' | 'HYBRID'

/**
 * Advisory metadata only: not read by any scorer, not editable here. Null
 * fields mean "no JD text yet, or extraction has not run for this
 * requisition" — not an error.
 */
export interface RoleArchetypeView {
  requisition_id: string
  archetype: RoleArchetype | null
  confidence: number | null
  signals: Record<string, number> | null
  classifier_version: string | null
  updated_at: string | null
}

export function getRoleArchetype(
  requisitionId: string,
  options?: RequestOptions,
): Promise<RoleArchetypeView> {
  return c.get<RoleArchetypeView>(`/admin/requisitions/${requisitionId}/role-archetype`, options)
}

// ── Admin: per-requisition archetype-FAMILY weight overrides (2026-08-19) ──────
//
// The EDITABLE twin of the read-only `RoleArchetypeView` above: an
// Admin-authored override of how much each archetype family (technical /
// relationship-facing / leadership) should count for THIS requisition,
// mirroring `RequisitionRankProfileView`'s exact shape. `HYBRID` is not a
// key here — it is a classifier OUTPUT STATE ("no family dominates"), never
// an editable weight leg. Not read by any scorer yet — see the backend
// route's own doc comment.

/** The three editable families, in DISPLAY order. Deliberately excludes
 * `HYBRID` — see the module comment above. */
export const ARCHETYPE_WEIGHT_FAMILIES = [
  'TECHNICAL',
  'RELATIONSHIP_FACING',
  'LEADERSHIP',
] as const
export type ArchetypeWeightFamily = (typeof ARCHETYPE_WEIGHT_FAMILIES)[number]

export interface RequisitionArchetypeWeightsHistoryItem {
  id: string
  version: number
  action: 'CREATED' | 'UPDATED' | 'RESET'
  actor_id: string
  weights_before: Record<string, number> | null
  weights_after: Record<string, number> | null
  rationale: string
  created_at: string
}

export interface RequisitionArchetypeWeightsView {
  requisition_id: string
  source: 'DEFAULT' | 'REQUISITION'
  /** Concurrency token for the compact row, including after a reset. */
  record_version: number | null
  profile_version: number | null
  weights: Record<string, number>
  rationale: string | null
  updated_at: string | null
  updated_by: string | null
  history: RequisitionArchetypeWeightsHistoryItem[]
}

export function getRequisitionArchetypeWeights(
  requisitionId: string,
  options?: RequestOptions,
): Promise<RequisitionArchetypeWeightsView> {
  return c.get<RequisitionArchetypeWeightsView>(
    `/admin/requisitions/${requisitionId}/archetype-weights`,
    options,
  )
}

export function updateRequisitionArchetypeWeights(
  requisitionId: string,
  weights: Record<string, number>,
  rationale: string,
  expectedVersion: number | null,
  options?: RequestOptions,
): Promise<RequisitionArchetypeWeightsView> {
  return c.post<RequisitionArchetypeWeightsView>(
    `/admin/requisitions/${requisitionId}/archetype-weights`,
    { weights, rationale, expected_version: expectedVersion },
    options,
  )
}

export function resetRequisitionArchetypeWeights(
  requisitionId: string,
  rationale: string,
  expectedVersion: number | null,
  options?: RequestOptions,
): Promise<RequisitionArchetypeWeightsView> {
  return c.post<RequisitionArchetypeWeightsView>(
    `/admin/requisitions/${requisitionId}/archetype-weights/reset`,
    { rationale, expected_version: expectedVersion },
    options,
  )
}

// ── Entity search (direction change, 2026-08-18) ────────────────────────────

/**
 * The app-wide "search, list, select" API over Meilisearch — one registered
 * type today (`skill`), feeding the manual skill-adding picker. Ranked and
 * typo-tolerant when Meilisearch answers; degrades to a Postgres substring
 * search server-side when it does not — this function has no visibility into
 * which happened, by design (the picker shouldn't need to know).
 */
export type EntitySearchType = 'skill'

export interface EntitySearchResult {
  id: string
  label: string
  sublabel?: string
  selectable: boolean
  disabled_reason?: string
}

export function searchEntities(
  type: EntitySearchType,
  q: string,
  limit = 20,
  options?: RequestOptions,
): Promise<EntitySearchResult[]> {
  return c.get<EntitySearchResult[]>(
    `/entity-search${qs({ type, q: q.trim() || undefined, limit })}`,
    options,
  )
}

// ── Requisition typed locks (POST /requisitions/:id/filters) ───────────────────

/** `OPEN` = no department lock; `LOCK` = only the listed departments. */
export type DeptLockMode = 'OPEN' | 'LOCK'
/** `FREE` = any location; `LIMITED` = only the listed cities. */
export type LocationLockMode = 'FREE' | 'LIMITED'

/**
 * The materialized filter block a requisition stores (API_SPEC
 * `POST /requisitions/:id/filters`).
 *
 * FB-4 — these ARE the department and location audience now, not a second copy
 * of it: `eligibility_filters` no longer carries `departments` / `locations`
 * (migration 0013 moved every stored value into these columns and deleted the
 * keys), so ONE control authors ONE representation. `functions` / `grades`
 * still live in `eligibility_filters`; all four dimensions survive.
 *
 * A NULL mode means NOT CONFIGURED — no constraint (an absent filter is never a
 * restriction), and the gate keeps its legacy JSON fallback for a hand-edited
 * row.
 *
 * FB-5: `keyword_must` / `keyword_should` are GONE. The gate and the scorer
 * that read them were removed and migration 0013 dropped the columns; the wire
 * schema REJECTS them, so they must not be typed as sendable here either.
 */
export interface RequisitionFilters {
  dept_lock_mode: DeptLockMode | null
  dept_lock_values: string[] | null
  location_lock_mode: LocationLockMode | null
  location_lock_values: string[] | null
  /**
   * Under LIMITED, also admit employees willing to relocate. NO EFFECT TODAY —
   * the relocate signal is hard-`false` until the Talent Marketplace ships the
   * standing preference (THRIVE_API_CONTRACT §6).
   */
  location_allow_relocate: boolean | null
  /**
   * D4 — NARROWING ONLY. `false` narrows a PLUS_ONE allow-list row to
   * SAME_GRADE; it can NEVER grant plus-one to a SAME_GRADE employee. The
   * employee allow-list row stays the authority.
   */
  grade_allow_plus_one: boolean | null
}

/** The filters body — every field optional; an omitted field stays NULL. */
export type RequisitionFiltersInput = Partial<RequisitionFilters>

/**
 * ADDITIVE: the materialized locks ride the requisition DTO once the backend
 * emits them (optional — older payloads predate the block, and the editor then
 * simply starts from "not configured").
 */
export interface Requisition {
  dept_lock_mode?: DeptLockMode | null
  dept_lock_values?: string[] | null
  location_lock_mode?: LocationLockMode | null
  location_lock_values?: string[] | null
  /**
   * NOT EMITTED by the requisition DTO today (only the four fields above are) —
   * declared for a payload that adds it. An editor therefore cannot READ this
   * back, which is why the dialog only SENDS it when the admin actually toggled
   * it: a blind `false` would silently clear a stored `true`.
   */
  location_allow_relocate?: boolean | null
  /** Same caveat as above — not on the requisition DTO; absent = follow the allow-list. */
  grade_allow_plus_one?: boolean | null
}

/**
 * `POST /requisitions/:id/filters` (HR_ADMIN, or the requisition's
 * assigned recruiter) — validate + materialize the typed eligibility locks.
 * A `LOCK`/`LIMITED` mode with empty values is a 400 `INVALID_FILTER_CONFIG`
 * (the editor mirrors that check client-side so the block never reaches the
 * wire malformed).
 */
export function setRequisitionFilters(
  requisitionId: string,
  input: RequisitionFiltersInput,
  options?: RequestOptions,
): Promise<RequisitionFilters> {
  return c.post<RequisitionFilters>(`/requisitions/${requisitionId}/filters`, input, options)
}

// ── FB-1: reading a requisition's target-entity set ────────────────────────────

/**
 * The requisition's TARGET ENTITY SET, read the one safe way (FB-1).
 *
 * `target_entity_ids` is the authority; the deprecated scalar is lifted to a
 * one-element set ONLY when the array is absent (a pre-FB-1 payload or an older
 * fixture). An EMPTY result means ENTITY-OPEN — employees of every company may
 * apply — and every surface must SAY that ("Any company"), never render it as a
 * missing value.
 */
export function targetEntityIds(
  requisition: Pick<Requisition, 'target_entity_ids' | 'target_entity_id'>,
): string[] {
  if (Array.isArray(requisition.target_entity_ids)) return requisition.target_entity_ids
  return requisition.target_entity_id ? [requisition.target_entity_id] : []
}

// ── FB-6: the centrally managed badge catalogue (DISPLAY-ONLY) ─────────────────

/**
 * A badge's presentation TONE — a token, never a hex value: the palette belongs
 * to the design system (`components/common/badge-chips.tsx`), not to the API.
 */
export type BadgeTone = 'NEUTRAL' | 'INFO' | 'SUCCESS' | 'WARNING' | 'DANGER' | 'ACCENT'

export const BADGE_TONES: readonly BadgeTone[] = [
  'NEUTRAL',
  'INFO',
  'SUCCESS',
  'WARNING',
  'DANGER',
  'ACCENT',
]

/**
 * Where a rendered chip came from: `DERIVED` = computed per card (HOT_JOB /
 * ACTIVELY_RECRUITING), `CUSTOM` = assigned from the catalogue. The employee
 * surfaces NEVER branch on this to render — to a viewer a badge is a badge.
 */
export type BadgeSource = 'DERIVED' | 'CUSTOM'

/**
 * ONE RENDERED CHIP — the uniform shape both kinds arrive in. It is what
 * `badge_items[]` carries on the job card / job detail, and what
 * `GET /requisitions/:id/badges` returns for the assigned set, so the authoring
 * UI and the board agree by construction.
 */
export interface BadgeChip {
  code: string
  label: string
  description: string | null
  /** One of {@link BADGE_TONES} (string-typed: a new token must not break old clients). */
  tone: string
  /** Lowercase kebab-case icon registry key, or null. */
  icon_key: string | null
  /**
   * Migration 0040 — a `#rrggbb` OVERRIDE for `tone`, or null/absent for the
   * normal case of "use the token". Present on the CHIP as well as the
   * catalogue row so a job card, which renders from chips alone, shows the
   * colour the admin actually chose.
   */
  custom_color?: string | null
  source: BadgeSource
  display_order: number
}

/**
 * ADDITIVE (FB-6): the UNIFORM badge list on a job card / detail — the derived
 * codes in `badges[]` projected into the same chip shape as the catalogue
 * badges assigned to the requisition, DERIVED first. OPTIONAL because older
 * payloads predate the field; renderers fall back to `badges[]`.
 *
 * `badges: string[]` is UNCHANGED (still the derived codes only).
 */
export interface Job {
  badge_items?: BadgeChip[]
}

/**
 * ADDITIVE: `GET /bookmarks` now annotates each surviving card with the SAME
 * ADVISORY match verdict the board and detail carry. Absent is normal (no
 * profile skills, unscoreable job) — render on `typeof match_percent ===
 * 'number'`, never on key presence.
 */
export interface BookmarkedJobCard {
  match_percent?: number
  matched_skills?: string[]
  score_breakdown?: ScoreBreakdown
}

/** A catalogue row (`GET /badges`). No PII — presentation configuration only. */
export interface Badge {
  id: string
  code: string
  label: string
  description: string | null
  tone: string
  icon_key: string | null
  /** Migration 0040 — `#rrggbb` override for `tone`; null = use the token. */
  custom_color: string | null
  /** `false` = RETIRED: the badge vanishes from job cards but assignments survive. */
  is_active: boolean
  display_order: number
  /**
   * WHO DECIDES this badge (migration 0035). `MANUAL` = an admin ticks it on a
   * requisition; `AUTO` = a rule decides it per posting. The requisition form
   * offers MANUAL only — an AUTO tick box would be overwritten by its rule.
   */
  badge_type: BadgeType
  /**
   * AUTO rows only — the ONE condition that decides this badge, evaluated per
   * posting from live facts. `null` on a MANUAL row.
   */
  rule: BadgeRule | null
  created_by: string
  created_at: string
  updated_at: string
}

/** `POST /badges/:id/delete` — the soft-deleted row + how many postings lost it. */
export interface BadgeDeleteResult extends Badge {
  unassigned: number
}

export interface CreateBadgeInput {
  /** UPPER_SNAKE, letter-first. HOT_JOB / ACTIVELY_RECRUITING are REFUSED (derived). */
  code: string
  label: string
  description?: string | null
  tone?: BadgeTone
  icon_key?: string | null
  /** `#rrggbb` override for `tone`; explicit `null` returns it to the token. */
  custom_color?: string | null
  is_active?: boolean
  display_order?: number
  /** MANUAL (hand-assigned) or AUTO (decided by `rule`). Defaults to MANUAL. */
  badge_type?: BadgeType
  /** REQUIRED when `badge_type` is AUTO, and must be absent/null when MANUAL. */
  rule?: BadgeRule | null
}

/** Partial update: absent = unchanged, explicit `null` clears a nullable string. */
export type UpdateBadgeInput = Partial<CreateBadgeInput>

/**
 * Codes an admin may not MINT as a new row: migration 0039 already created them
 * as AUTO rows, and a second row with the same code would be ambiguous. They
 * are fully EDITABLE as the rows they are — only re-creating them is refused.
 */
export const RESERVED_BADGE_CODES: readonly string[] = ['HOT_JOB', 'ACTIVELY_RECRUITING']

// ── The automatic-badge rule (migration 0039) ──────────────────────────────────

/** The facts a rule may test. */
export const BADGE_FACTS = ['applicant_count', 'days_since_opened'] as const
export type BadgeFact = (typeof BADGE_FACTS)[number]

/** The comparisons a rule may make. */
export const BADGE_OPS = ['gte', 'lte'] as const
export type BadgeOp = (typeof BADGE_OPS)[number]

/**
 * ONE condition — the whole grammar. No and/or, no nesting, no expressions:
 * a closed grammar is the only kind that can be validated up front, explained
 * back to a human, and put behind a control an admin can use without a manual.
 */
export interface BadgeRule {
  fact: BadgeFact
  op: BadgeOp
  value: number
}

/** The fact's label and the unit its number is in, for the console's controls. */
export const BADGE_FACT_META: Record<BadgeFact, { label: string; unit: string }> = {
  applicant_count: { label: 'Applications received', unit: 'applications' },
  days_since_opened: { label: 'Days since the posting opened', unit: 'days' },
}

/**
 * Each comparison as BOTH a symbol and the words it stands for.
 *
 * The symbol is what the control shows — `≥` reads faster than "is at least",
 * and two symbols side by side make the choice a glance rather than a dropdown
 * you must open to see the other option. The words are the accessible name and
 * the tooltip, because a symbol alone is not a label: a screen reader announcing
 * "greater than or equal" is the whole point, and the sentence under the control
 * restates the rule in full either way.
 */
export const BADGE_OP_META: Record<BadgeOp, { symbol: string; label: string }> = {
  gte: { symbol: '≥', label: 'is at least' },
  lte: { symbol: '≤', label: 'is at most' },
}

/** The rule in plain words — the same sentence the backend renders. */
export function describeBadgeRule(rule: BadgeRule | null): string {
  if (!rule) return 'Not configured — this badge never shows.'
  const n = rule.value
  if (rule.fact === 'applicant_count') {
    return rule.op === 'gte'
      ? `Shown when ${n} or more people have applied.`
      : `Shown while ${n} or fewer people have applied.`
  }
  return rule.op === 'gte'
    ? `Shown once the posting has been open for ${n} days or more.`
    : `Shown for the first ${n} days after the posting opens.`
}

/** Who decides a badge (migration 0035) — see {@link Badge.badge_type}. */
export type BadgeType = 'MANUAL' | 'AUTO'

/**
 * `GET /badges` (HR_ADMIN / RECRUITER / HR_PARTNER). `is_active` is
 * OPTIONAL AND UNSET by default — the console must be able to see retired rows
 * without knowing to ask; the requisition picker sends `is_active: true`.
 */
export function listBadges(
  filters: ListQuery & { is_active?: boolean; badge_type?: BadgeType } = {},
  options?: RequestOptions,
): Promise<PagedResponse<Badge[]>> {
  return c.getPage<Badge[]>(`/badges${qs(filters)}`, options)
}

/** `POST /badges` (HR_ADMIN) — 409 CONFLICT on a duplicate code. */
export function createBadge(input: CreateBadgeInput, options?: RequestOptions): Promise<Badge> {
  return c.post<Badge>('/badges', input, options)
}

/** `POST /badges/:id/update` (HR_ADMIN) — at least one field required. */
export function updateBadge(
  id: string,
  input: UpdateBadgeInput,
  options?: RequestOptions,
): Promise<Badge> {
  return c.post<Badge>(`/badges/${id}/update`, input, options)
}

/** `POST /badges/:id/delete` (HR_ADMIN) — soft delete + cascade unassign. */
export function deleteBadge(id: string, options?: RequestOptions): Promise<BadgeDeleteResult> {
  return c.post<BadgeDeleteResult>(`/badges/${id}/delete`, undefined, options)
}

/** `GET /requisitions/:id/badges` — the CUSTOM chips assigned to one posting. */
export function listRequisitionBadges(
  requisitionId: string,
  options?: RequestOptions,
): Promise<BadgeChip[]> {
  return c.get<BadgeChip[]>(`/requisitions/${requisitionId}/badges`, options)
}

/** `POST /requisitions/:id/badges/assign` — IDEMPOTENT; returns the new set. */
export function assignRequisitionBadges(
  requisitionId: string,
  badgeIds: string[],
  options?: RequestOptions,
): Promise<BadgeChip[]> {
  return c.post<BadgeChip[]>(
    `/requisitions/${requisitionId}/badges/assign`,
    { badge_ids: badgeIds },
    options,
  )
}

/** `POST /requisitions/:id/badges/unassign` — IDEMPOTENT; returns the new set. */
export function unassignRequisitionBadges(
  requisitionId: string,
  badgeIds: string[],
  options?: RequestOptions,
): Promise<BadgeChip[]> {
  return c.post<BadgeChip[]>(
    `/requisitions/${requisitionId}/badges/unassign`,
    { badge_ids: badgeIds },
    options,
  )
}

// ── Entity logo upload (the SECOND way to set the same column) ─────────────────

/**
 * `logo_url` holds ONE shape and always has: an absolute http(s) URL. Pasting a
 * URL sets it; uploading an image also sets it — the bytes go to a dedicated
 * PUBLIC bucket and the column keeps that object's plain, stable, cacheable
 * URL. So a client renders `logo_url` and is done: there is no presigned
 * companion to prefer, and nothing expires under a job board that shows the
 * same logo on twenty cards.
 */

/** The renderable logo URL for an entity row (null when it has none). */
export function entityLogoSrc(
  entity: { logo_url: string | null } | null | undefined,
): string | null {
  if (!entity) return null
  return entity.logo_url ?? null
}

/** The upload's byte cap + accepted image types (mirrors the backend allowlist). */
export const ENTITY_LOGO_MAX_BYTES = 2 * 1024 * 1024
export const ENTITY_LOGO_ACCEPT = '.png,.jpg,.jpeg,.webp,.gif'
export const ENTITY_LOGO_MIME_TYPES: readonly string[] = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/gif',
]
/** The accepted formats, worded exactly as the backend words them. */
export const ENTITY_LOGO_FORMATS_TEXT = 'PNG, JPEG, WEBP or GIF'

/**
 * `POST /entities/:id/logo` (HR_ADMIN, multipart field `file`): store the
 * image bytes and point `logo_url` at the stored object's PUBLIC URL. ADDITIVE
 * — pasting a URL on create/update still works and still wins if sent
 * afterwards (one column, last write wins).
 *
 * The upload is world-readable once stored, so the backend refuses anything
 * whose MAGIC BYTES are not a raster image, and refuses SVG outright (it can
 * carry script). A pasted SVG URL is fine — it is served by whoever hosts it.
 */
export function uploadEntityLogo(
  entityId: string,
  file: File,
  options?: RequestOptions,
): Promise<Entity> {
  const form = new FormData()
  form.append('file', file)
  return c.postMultipart<Entity>(`/entities/${entityId}/logo`, form, options)
}

// ── WAVE M1: the DEPARTMENT master (the authoring vocabulary) ──────────────────

/**
 * One department-master row (`GET /departments`). No PII — a department is
 * organisational configuration: a code, a name, a description.
 *
 * NOT an eligibility input. The department AUDIENCE of a posting is its typed
 * department lock (`dept_lock_mode` / `dept_lock_values`, compared against the
 * ROSTER department); this master only supplies the vocabulary the authoring
 * screen picks from, and nothing here narrows who may apply.
 */
export interface Department {
  id: string
  /**
   * WAVE M7 — the group company this department belongs to. Ownership is
   * EXCLUSIVE, so a department several companies use is one row per company.
   * Null only on a pre-0034 row.
   */
  entity_id: string | null
  /** UPPER_SNAKE, letter-first — the stable business key a requisition stores. */
  code: string
  /** The DISPLAY name; renaming it re-stamps the label on every live posting. */
  name: string
  description: string | null
  /** `false` = RETIRED: gone from the authoring picker, kept on live postings. */
  is_active: boolean
  display_order: number
  created_by: string
  updated_by?: string | null
  created_at: string
  updated_at: string
}

/**
 * `POST /departments/:id/delete` — the soft-deleted row plus how many live
 * requisitions still reference it. Those requisitions are NOT changed (they
 * keep rendering their stored label), so the count is an impact REPORT, not a
 * cascade receipt.
 */
export interface DepartmentDeleteResult extends Department {
  referencing_requisitions: number
}

export interface CreateDepartmentInput {
  /** WAVE M7 — the OWNING company. */
  entity_id: string
  /** UPPER_SNAKE, letter-first, 2–50. */
  code: string
  name: string
  description?: string | null
  is_active?: boolean
  display_order?: number
}

/**
 * Partial update: absent = unchanged, explicit `null` clears the description.
 *
 * WAVE M7 — `entity_id` is deliberately NOT updatable. Moving a department
 * between companies would re-home the postings that already carry its code;
 * create one under the other company instead.
 */
export type UpdateDepartmentInput = Partial<Omit<CreateDepartmentInput, 'entity_id'>>

/**
 * `GET /departments` (HR_ADMIN / RECRUITER / HR_PARTNER). `is_active` is
 * OPTIONAL AND UNSET by default — the admin console must be able to see the
 * rows it retired without knowing to ask; the authoring picker sends
 * `is_active: true`.
 *
 * The server's page ceiling is 100 (it is also the default), because this list
 * IS the authoring dropdown and a picker silently showing the first 20 of 40
 * departments is how an author ends up typing a near-duplicate again. Read
 * `meta.pagination.totalItems` to find out whether a page was the whole master.
 */
export function listDepartments(
  filters: ListQuery & {
    /**
     * WAVE M7 — the OWNING-COMPANY facet. MANDATORY for the requisition
     * authoring picker: a posting may only use its own company’s departments.
     */
    entity_id?: string
    is_active?: boolean
  } = {},
  options?: RequestOptions,
): Promise<PagedResponse<Department[]>> {
  return c.getPage<Department[]>(`/departments${qs(filters)}`, options)
}

/** `POST /departments` (HR_ADMIN) — duplicate code OR name → 409 CONFLICT. */
export function createDepartment(
  input: CreateDepartmentInput,
  options?: RequestOptions,
): Promise<Department> {
  return c.post<Department>('/departments', input, options)
}

/** `POST /departments/:id/update` (HR_ADMIN) — at least one field required. */
export function updateDepartment(
  id: string,
  input: UpdateDepartmentInput,
  options?: RequestOptions,
): Promise<Department> {
  return c.post<Department>(`/departments/${id}/update`, input, options)
}

/**
 * `POST /departments/:id/delete` (HR_ADMIN) — SOFT delete, NO cascade:
 * requisitions carrying this department keep their stored label and keep
 * rendering it. The result reports how many are affected.
 */
export function deleteDepartment(
  id: string,
  options?: RequestOptions,
): Promise<DepartmentDeleteResult> {
  return c.post<DepartmentDeleteResult>(`/departments/${id}/delete`, undefined, options)
}

// ── WAVE M2: the ROLE master (role_id → job description) ───────────────────────

/**
 * One role-master row (`GET /role-master`). This is the JOB-ROLE catalogue —
 * a role code, its title and the job description that code carries. It has
 * NOTHING to do with `GET /roles`, which grants a person HR-Partner /
 * Recruiter / Administrator ACCESS.
 *
 * `jd_text` is COPIED onto a requisition when its `role_id` resolves here. It
 * is a copy on purpose: a posting is a durable record people apply against, so
 * editing the master must not rewrite a JD under the candidates already in that
 * posting's pipeline. Re-selecting the role on a DRAFT is the explicit,
 * audited "pull the latest JD".
 */
export interface RoleMaster {
  id: string
  /**
   * WAVE M6 (migration 0031) — the group company that OWNS this role. A
   * requisition may only select a role belonging to its POSTING entity, so the
   * authoring picker MUST filter on this. `null` only on a pre-0031 row.
   */
  entity_id: string | null
  /**
   * The business key. GENERATED since Wave M6 as `{short_code}-RL-{6 digits}`
   * (`AXMF-RL-000123`) for roles created on the console — which makes it
   * OPAQUE, so search on `role_name`, never on this. Roles that arrived from
   * outside keep their own shape: the bulk upload adopts the counterparty's
   * key, and an inline-JD posting mints `AUTO-<slug>-<hex>`.
   */
  role_id: string
  role_name: string
  jd_text: string
  /**
   * WAVE M5 (migration 0028) — the STRUCTURED half of the JD, the same five-key
   * shape the requisition used to hold. `null` = the role is text-only.
   */
  jd_sections: JdSections | null
  /**
   * WAVE M5 (migration 0028) — PROVENANCE: how this role came to exist.
   * `string` on the wire (a CHECK-constrained column, not an enum), so read it
   * through {@link roleMasterSource} rather than comparing it raw.
   */
  source: string
  department: string | null
  function: string | null
  /** `false` = RETIRED: unpickable for NEW postings; existing ones keep their JD. */
  is_active: boolean
  created_by: string
  updated_by?: string | null
  created_at: string
  updated_at: string
}

/**
 * The `GET /role-master/resolve` projection — exactly what the authoring screen
 * needs to render the JD read-only BEFORE saving, and nothing more. A `role_id`
 * that matches no ACTIVE master row is a 404 (`NOT_FOUND`), which is a normal
 * authoring state, not an error: the role is then an inert display passthrough
 * and the JD stays hand-authored.
 */
export interface ResolvedRole {
  id: string
  role_id: string
  role_name: string
  jd_text: string
  /** WAVE M5 — the structured half, so a preview renders what the posting will. */
  jd_sections?: JdSections | null
}

/**
 * WAVE M5 (migration 0028) — the three ways a role row comes to exist. This is
 * NOT decoration: it decides who may edit a role's JD and from where.
 *
 *  • CURATED      — authored on the role-master console (or seeded). Shared
 *                   governance: an edit reaches every posting on the role, so
 *                   the requisition form REFUSES to edit it and points there.
 *  • AUTO_CREATED — minted from an inline JD typed on a requisition. The text is
 *                   that posting's own, so the requisition form may edit it in
 *                   place; sending its author to another screen to fix their own
 *                   typo would be an absurdity produced by where the bytes live.
 *  • IMPORTED     — created by the role-master bulk upload. Governed, like
 *                   CURATED.
 */
export const ROLE_MASTER_SOURCES = ['CURATED', 'AUTO_CREATED', 'IMPORTED'] as const
export type RoleMasterSource = (typeof ROLE_MASTER_SOURCES)[number]

/**
 * Read the provenance off a role DTO. Anything unrecognised (an older payload,
 * a value added server-side after this client shipped) reads as CURATED — the
 * GOVERNED, read-only direction. Guessing the other way would let this form
 * rewrite a shared JD it has no business touching.
 */
export function roleMasterSource(role: { source?: string | null } | null | undefined): RoleMasterSource {
  const raw = role?.source
  return (ROLE_MASTER_SOURCES as readonly string[]).includes(raw ?? '')
    ? (raw as RoleMasterSource)
    : 'CURATED'
}

/**
 * The role-master bulk report. The house `{total, succeeded, failed, failures}`
 * shape plus the UPSERT split — the counterparty re-uploads a corrected sheet,
 * so "how many were new" and "how many were overwritten" are different facts.
 */
export interface RoleMasterBulkReport {
  total: number
  succeeded: number
  created: number
  updated: number
  failed: number
  failures: RowFailure[]
}

export interface CreateRoleMasterInput {
  /**
   * WAVE M6 (migration 0031) — the OWNING company. This REPLACED `role_id` on
   * the create body: the key is now GENERATED from this entity's short code
   * (`AXMF-RL-000123`), so the caller names an owner and the server names the
   * role. An entity with no short code is refused rather than given a
   * prefixless key.
   */
  entity_id: string
  role_name: string
  /** REQUIRED and non-empty: a blank JD is what this master exists to prevent. */
  jd_text: string
  /** WAVE M5 — the structured half beside the text sink it flattens to. */
  jd_sections?: JdSections | null
  department?: string | null
  function?: string | null
  is_active?: boolean
}

/**
 * Partial update: absent = unchanged, explicit `null` clears department /
 * function. `jd_text` is NOT nullable — see {@link CreateRoleMasterInput}.
 */
export type UpdateRoleMasterInput = Partial<Omit<CreateRoleMasterInput, 'entity_id'>> & {
  /** WAVE M5 — PROMOTE an incidental role into the governed catalogue. */
  source?: RoleMasterSource
  /**
   * WAVE M6 — the update body deliberately CANNOT change `entity_id`. A shared
   * role changing company would re-own a catalogue entry that live requisitions
   * already select; only an AUTO_CREATED role moves, and only by following its
   * own posting when that posting is re-homed.
   */
  role_id?: string
}

/**
 * `GET /role-master` (HR_ADMIN / RECRUITER / HR_PARTNER). `is_active` is
 * OPTIONAL AND UNSET by default — the console lists retired rows too; the
 * authoring picker sends `is_active: true`. The server's page ceiling is 100.
 */
export function listRoleMaster(
  filters: ListQuery & {
    /**
     * WAVE M6 — the OWNING-ENTITY facet. The console's primary filter, and
     * MANDATORY for the requisition-authoring picker: a posting may only select
     * a role its own company owns, so a picker that omitted this would offer
     * roles the server will refuse.
     */
    entity_id?: string
    is_active?: boolean
    department?: string
    function?: string
    /** WAVE M5 — the PROVENANCE facet (CURATED | AUTO_CREATED | IMPORTED). */
    source?: RoleMasterSource
  } = {},
  options?: RequestOptions,
): Promise<PagedResponse<RoleMaster[]>> {
  return c.getPage<RoleMaster[]>(`/role-master${qs(filters)}`, options)
}

/**
 * `GET /role-master/:id` — ONE role by its UUID, as the full DTO.
 *
 * The authoring surface needs it for exactly one fact `GET /role-master/resolve`
 * does not carry: {@link RoleMaster.source}. Whether a requisition may edit the
 * JD it is linked to turns on that field, and it must be READ, never inferred
 * from the key's shape or from which page of the catalogue happened to load.
 */
export function getRoleMaster(id: string, options?: RequestOptions): Promise<RoleMaster> {
  return c.get<RoleMaster>(`/role-master/${id}`, options)
}

/**
 * `GET /role-master/resolve?role_id=` — "what JD does this role_id carry RIGHT
 * NOW?". The AUTHORING preview: it never writes anything, and it answers for a
 * role that is not on the picker's loaded page. A 404 means the role resolves
 * to nothing; callers must treat that as a state, not a failure.
 */
export function resolveRole(roleId: string, options?: RequestOptions): Promise<ResolvedRole> {
  return c.get<ResolvedRole>(`/role-master/resolve${qs({ role_id: roleId })}`, options)
}

/** `POST /role-master` (HR_ADMIN) — duplicate role_id → 409 CONFLICT. */
export function createRoleMaster(
  input: CreateRoleMasterInput,
  options?: RequestOptions,
): Promise<RoleMaster> {
  return c.post<RoleMaster>('/role-master', input, options)
}

/** `POST /role-master/:id/update` (HR_ADMIN) — at least one field required. */
export function updateRoleMaster(
  id: string,
  input: UpdateRoleMasterInput,
  options?: RequestOptions,
): Promise<RoleMaster> {
  return c.post<RoleMaster>(`/role-master/${id}/update`, input, options)
}

/**
 * `POST /role-master/:id/delete` (HR_ADMIN) — SOFT delete, no cascade and
 * no reference check.
 *
 * A requisition does NOT copy the JD (migration 0028 reversed 0023's
 * copy-at-selection): it stores `role_id` as a BUSINESS KEY — not a foreign key
 * — and every reader resolves the description through the role master. What
 * makes a delete safe is therefore not a private copy but the resolver, which
 * is deliberately unfiltered on `is_deleted`: a soft-deleted role still answers
 * for the postings pointing at it, so they stay complete. The delete only takes
 * the role out of the picker for NEW postings.
 *
 * The corollary is the part worth stating: because it IS a live reference,
 * EDITING a role rewrites the JD of every posting that references it.
 */
export function deleteRoleMaster(id: string, options?: RequestOptions): Promise<RoleMaster> {
  return c.post<RoleMaster>(`/role-master/${id}/delete`, undefined, options)
}

/**
 * `POST /role-master/bulk-upload` (HR_ADMIN, multipart field `file`) —
 * CSV/TSV or .xlsx, content-sniffed server-side (a workbook prefers its
 * ROLE_MASTER sheet), ≤5MB. Header-driven and alias-tolerant, so `role_id`,
 * `ROLE_ID` and `Role ID` are one column.
 *
 * It is an UPSERT on `role_id`: an existing role is UPDATED, a new one CREATED.
 * A file missing a REQUIRED column aborts as a whole (nothing half-lands);
 * individual bad rows fail individually and are reported.
 */
export function uploadRoleMaster(
  file: File,
  options?: RequestOptions,
): Promise<RoleMasterBulkReport> {
  const form = new FormData()
  form.append('file', file)
  return c.postMultipart<RoleMasterBulkReport>('/role-master/bulk-upload', form, options)
}

// ── WAVE S1/F3: the SKILL master (the taxonomy behind every match) ─────────────

/**
 * One skill-master row (`GET /skill-master`). This is the canonical taxonomy
 * every extracted JD phrase resolves against — ~3,857 rows, HR_ADMIN-only,
 * and the input to roughly 70% of the match blend.
 *
 * NO PII, by construction: a row is a taxonomy id, a name, a category, a
 * definition and some provenance. It belongs to no employee and no subsidiary.
 *
 * `is_embedded` is the field that makes this master unlike its siblings. A
 * department is usable the moment it is saved; a SKILL is usable only once the
 * backfill worker has given it a VECTOR, because extraction resolves phrases by
 * nearest neighbour over the embedded rows alone. A row with `is_embedded:
 * false` is loaded, correct, and invisible to matching. The vector BYTES are
 * never serialised — only the fact that they exist, which model wrote them and
 * when.
 */
export interface SkillMaster {
  id: string
  /** The upstream taxonomy's own key (natural key; not a UUID). */
  skill_id: string
  skill_name: string
  /** `false` = RETIRED upstream (IS_DELETED=1) — kept, and out of the matcher. */
  is_active: boolean
  skill_category: string | null
  /** The ~60-word definition. Stored FOR HUMANS — deliberately not embedded. */
  skill_description: string | null
  /** functional | technical | behavioral (free text — the vocabulary is upstream's). */
  skill_bucket: string | null
  similar_skill_ids: string[]
  lightcast_id: string | null
  mapping: string | null
  /** Does this row carry a vector? If not, extraction cannot see it. */
  is_embedded: boolean
  embedding_model: string | null
  embedded_at: string | null
  created_at: string | null
  updated_at: string | null
}

/** One embedding model's share of the embedded rows. */
export interface SkillMasterModelStat {
  model: string | null
  count: number
  last_embedded_at: string | null
}

/**
 * `GET /skill-master/stats` — the taxonomy's READINESS in one read.
 *
 * The list is paginated (≤100 of ~3,857), so no page can answer the only two
 * questions worth asking about this table: is it loaded, and can extraction SEE
 * it? `embedded` is the population of the matcher's warm matrix, not a row
 * attribute summed up — so it is the honest answer to the second one.
 *
 * `embed_max_per_pass` / `embed_pass_interval_minutes` are the backfill drain's
 * real bounds, served rather than hardcoded here, so the console can state a
 * forecast that stays true when the cadence setting changes.
 */
export interface SkillMasterStats {
  total: number
  active: number
  retired: number
  embedded: number
  unembedded: number
  last_embedded_at: string | null
  models: SkillMasterModelStat[]
  embed_max_per_pass: number
  /** `null` = the cadence could not be resolved; render "unknown", never a guess. */
  embed_pass_interval_minutes: number | null
}

/**
 * The skill-master bulk report. The house `{total, succeeded, failed, failures}`
 * shape plus the counters an IDEMPOTENT import needs to be readable: a re-run of
 * the same file reads `inserted: 0, updated: 0, unchanged: N`.
 *
 * `warnings` is SEPARATE from `failures` on purpose — a warning row LANDED with
 * something cosmetic dropped, a failure row did not land at all, and collapsing
 * the two would make a 3,857-row import unreviewable.
 */
export interface SkillMasterBulkReport {
  total: number
  succeeded: number
  failed: number
  inserted: number
  updated: number
  unchanged: number
  /** Rows whose name changed, so their stale vector was cleared and is re-queued. */
  reembed_queued: number
  /** ACTIVE rows after the import — the "is extraction unblocked?" probe. */
  active_after: number
  failures: RowFailure[]
  warnings: { row: number; warning: string }[]
}

/**
 * `GET /skill-master` (HR_ADMIN only — no recruiter or employee screen
 * renders the raw taxonomy). `is_active` and `is_embedded` are both OPTIONAL AND
 * UNSET by default: the console must be able to see the rows a refresh retired
 * and the rows the backfill has not reached without knowing to ask for them.
 */
export function listSkillMaster(
  filters: ListQuery & {
    /** Case-insensitive substring of the skill NAME. */
    q?: string
    skill_category?: string
    skill_bucket?: string
    is_active?: boolean
    is_embedded?: boolean
  } = {},
  options?: RequestOptions,
): Promise<PagedResponse<SkillMaster[]>> {
  return c.getPage<SkillMaster[]>(`/skill-master${qs(filters)}`, options)
}

/** `GET /skill-master/stats` (HR_ADMIN) — see {@link SkillMasterStats}. */
export function getSkillMasterStats(options?: RequestOptions): Promise<SkillMasterStats> {
  return c.get<SkillMasterStats>('/skill-master/stats', options)
}

/**
 * `POST /skill-master/bulk-upload` (HR_ADMIN, multipart field `file`) —
 * CSV or .xlsx, content-sniffed server-side (a workbook prefers its SKILL_MASTER
 * sheet), ≤5MB.
 *
 * IDEMPOTENT, matched on `skill_id`: an unchanged row is not written at all, so
 * re-uploading the same file is free and is the right move when you are unsure
 * whether the import ran. A file missing a REQUIRED column aborts as a whole
 * (importing 3,857 nameless rows is not a partial success); individual bad rows
 * fail individually and are reported with their row number.
 */
export function uploadSkillMaster(
  file: File,
  options?: RequestOptions,
): Promise<SkillMasterBulkReport> {
  const form = new FormData()
  form.append('file', file)
  return c.postMultipart<SkillMasterBulkReport>('/skill-master/bulk-upload', form, options)
}

// ── Manual AXIS_BANK Thrive master sync ─────────────────────────────────────

export type MasterSyncDataset = 'skills' | 'departments'

export interface MasterSyncValue {
  name: string
  is_active: boolean
  category?: string | null
  bucket?: string | null
  code?: string | null
}

export interface MasterSyncChange {
  key: string
  action: 'insert' | 'update'
  changed_fields: string[]
  before: MasterSyncValue | null
  after: MasterSyncValue
}

export interface MasterSyncPreview {
  dataset: MasterSyncDataset
  entity_code: 'AXIS_BANK'
  fingerprint: string
  upstream_total: number
  inserted: number
  updated: number
  unchanged: number
  changes: MasterSyncChange[]
}

export interface MasterSyncApplyResult extends MasterSyncPreview {
  applied: number
  reembed_queued: number
}

export interface MasterSyncQueued {
  dataset: 'skills'
  entity_code: 'AXIS_BANK'
  fingerprint: string
  message_id: string
  queued: true
}

/** Fetch a write-free diff directly from Thrive Sync; no manifest is involved. */
export function previewMasterSync(
  dataset: MasterSyncDataset,
  options?: RequestOptions,
): Promise<MasterSyncPreview> {
  return c.post<MasterSyncPreview>(`/master-sync/${dataset}/preview`, undefined, options)
}

/** Queue skill apply through BullMQ; department apply remains synchronous. */
export function applyMasterSync(
  dataset: MasterSyncDataset,
  fingerprint: string,
  options?: RequestOptions,
): Promise<MasterSyncApplyResult | MasterSyncQueued> {
  return c.post<MasterSyncApplyResult | MasterSyncQueued>(`/master-sync/${dataset}/apply`, { fingerprint }, options)
}

// ── The recommender's Meilisearch index (HR_ADMIN) ──────────────────────

/**
 * Health of the search projection.
 *
 * TWO SWITCHES, REPORTED SEPARATELY, because they are different problems with
 * different fixes: `enabled` is the OPERATOR's (`recommender_meili_enabled`),
 * while an unreachable index is the DEPLOYMENT's. A console that collapsed them
 * into one "off" would send someone to edit a settings row when the real
 * answer is that Meilisearch is down.
 */
export interface RecommenderIndexStatus {
  index_name: string
  /** False = Meilisearch is unreachable and every number below is meaningless. */
  reachable: boolean
  documents: number
  live_requisitions: number
  /**
   * `documents - live_requisitions`. Catches postings never indexed and never
   * removed — and NOTHING else. An UPDATE changes neither count, so `drift: 0`
   * is not proof the index is correct; only reconcile answers that.
   */
  drift: number
  is_indexing: boolean
  embedding_dimensions: number
  enabled: boolean
}

export interface RecommenderReconcileResult {
  reachable: boolean
  checked: number
  /** Live in Postgres, absent from the index. */
  missing: number
  /** Indexed copy older than the row — the class `drift` cannot see. */
  stale: number
  /** In the index, no longer live in Postgres. */
  orphaned: number
  repaired: number
  duration_ms: number
  sample: { missing: string[]; stale: string[]; orphaned: string[] }
}

export interface RecommenderRebuildResult {
  indexed: number
  /** Indexed with NO vector: no extracted skills yet, so keyword-only. */
  without_vector: number
  batches: number
  duration_ms: number
  cleared: boolean
  /** The index actually built — the live one, or `targetIndexName` when given. */
  index_name: string
}

/** `GET /admin/recommender/index` */
export function getRecommenderIndexStatus(
  options?: RequestOptions,
): Promise<RecommenderIndexStatus> {
  return c.get<RecommenderIndexStatus>('/admin/recommender/index', options)
}

/**
 * `POST /admin/recommender/index/reconcile` — compare every live posting against
 * its document by `updated_at`.
 *
 * REPORT-ONLY unless `repair`. With `repair` it re-projects what it finds and
 * waits for the index to settle first, so the numbers are true when you read
 * them rather than describing writes still in flight.
 */
export function reconcileRecommenderIndex(
  repair = false,
  options?: RequestOptions,
): Promise<RecommenderReconcileResult> {
  return c.post<RecommenderReconcileResult>('/admin/recommender/index/reconcile', { repair }, options)
}

/**
 * `POST /admin/recommender/index/rebuild` — re-derive the index from Postgres.
 *
 * `clear` empties it first, which is the ONLY way to drop documents whose
 * postings vanished while the projection was unmaintained — but it leaves the
 * index empty for the duration, so it is opt-in rather than the default.
 *
 * `targetIndexName`, when given, builds into THAT index instead of the live
 * configured one — for validating a schema/settings change (a new field, say)
 * before ever pointing live traffic at it. The live index is untouched.
 */
export function rebuildRecommenderIndex(
  clear = false,
  options?: RequestOptions,
  targetIndexName?: string,
): Promise<RecommenderRebuildResult> {
  return c.post<RecommenderRebuildResult>(
    '/admin/recommender/index/rebuild',
    { clear, ...(targetIndexName ? { target_index_name: targetIndexName } : {}) },
    options,
  )
}

/** `POST /admin/recommender/index/requisition` — project ONE posting now. */
export function reindexRequisition(
  requisitionId: string,
  options?: RequestOptions,
): Promise<{ requisition_id: string; outcome: 'indexed' | 'removed' }> {
  return c.post<{ requisition_id: string; outcome: 'indexed' | 'removed' }>(
    '/admin/recommender/index/requisition',
    { requisition_id: requisitionId },
    options,
  )
}

// ── Skill-extraction backlog + triggers (HR_ADMIN) ──────────────────────

/**
 * The extraction engine registry (mirrors the backend's pinned
 * `EXTRACTION_ENGINE_CODES`). The engine picker on the Skill extraction card
 * iterates this list — a small, deliberate mirror rather than a fetch,
 * same pattern as {@link SCORER_CODES}: adding a third engine means adding a
 * code (and a label) here, one small FE change alongside the backend one that
 * registers it, not a runtime lookup for a two-item list that changes rarely.
 */
export const EXTRACTION_ENGINE_CODES = [
  'PHRASE_CENTRIC',
  'SKILL_CENTRIC',
  'HYBRID_FUSION',
  'TAXONOMY_FIRST',
] as const
export type ExtractionEngineCode = (typeof EXTRACTION_ENGINE_CODES)[number]
export const EXTRACTION_ENGINE_LABELS: Record<ExtractionEngineCode, string> = {
  PHRASE_CENTRIC: 'Phrase-centric',
  SKILL_CENTRIC: 'Skill-centric',
  // 2026-08-18 night session: multi-signal fusion (fuzzy short-circuit + RRF
  // over a lexical + semantic signal) over a RAKE-style phrase list. Seeded
  // off — selectable here for comparison exactly like Skill-centric.
  HYBRID_FUSION: 'Hybrid fusion',
  // 2026-08-19: built off two independent external reviews (Codex/OpenAI,
  // Gemini) of Hybrid fusion, both converging on the same fix — scan the JD
  // ONCE for every taxonomy skill name (Aho-Corasick), fall back to a top-K
  // embedding match only for text that scan does not cover. Seeded off —
  // selectable here for comparison exactly like the other two.
  TAXONOMY_FIRST: 'Taxonomy-first',
}
/** Settings key the picker writes to — kept alongside the codes it validates against. */
export const SKILL_EXTRACTION_ENGINE_SETTING = 'skill_extraction_engine'

/**
 * Is extraction keeping up?
 *
 * `awaiting_extraction` is the number that matters. A posting with no extracted
 * skills contributes nothing to `skill_overlap` (0.40 of the match blend), has
 * no job vector, and indexes as keyword-only — it looks completely normal on the
 * board while being invisible to half the recommender.
 */
export interface ExtractionStatus {
  total_requisitions: number
  extracted: number
  awaiting_extraction: number
  skill_rows: number
  /** Pinned at the extraction cap ⇒ a quality smell, not a success. */
  average_skills_per_posting: number
  last_extracted_at: string | null
  /** False ⇒ no real queue backend, so nothing processes in the background. */
  queue_enabled: boolean
  /** False ⇒ bulk replacement extraction is locked by a DB setting. */
  force_backfill_enabled: boolean
  /** The `ExtractionEngine` `skill_extraction_engine` currently resolves to —
   * what actually runs, not just what the setting reads back. */
  active_engine: { code: string; description: string }
  /** Live BullMQ state; null when the in-memory broker is selected. */
  queue: {
    waiting: number
    active: number
    delayed: number
    completed: number
    failed: number
    paused: number
  } | null
  sample_awaiting: Array<{ id: string; job_code: string | null; title: string }>
}

export function getExtractionStatus(options?: RequestOptions): Promise<ExtractionStatus> {
  return c.get<ExtractionStatus>('/admin/recommender/extraction', options)
}

/** Queue ONE posting. Idempotent unless `force`. */
export function enqueueExtraction(
  requisitionId: string,
  force = false,
  options?: RequestOptions,
): Promise<{ requisition_id: string; enqueued: boolean }> {
  return c.post('/admin/recommender/extraction/enqueue', { requisition_id: requisitionId, force }, options)
}

/** Queue waiting postings, or every live posting when `force` requests replacement extraction. */
export function backfillExtraction(
  limit = 500,
  force = false,
  options?: RequestOptions,
): Promise<{ enqueued: number; failed: number; remaining: number }> {
  return c.post('/admin/recommender/extraction/backfill', { limit, force }, options)
}

/** One skill-master embedding pass NOW, rather than waiting for the tick. */
export function drainMasterEmbeddings(
  options?: RequestOptions,
): Promise<{ embedded: number; failed: number; degraded: boolean; active: number }> {
  return c.post('/admin/recommender/extraction/embed-master', {}, options)
}

// ── The SEARCH-DATA surface (HR_ADMIN) ──────────────────────────────────
// What is actually IN the engine, and how to put it there: the index catalog,
// the document browser, and the two SEEDED datasets (gazetteer, synonyms).
// READ-ONLY over documents — a job document only changes by way of the Postgres
// row it derives from.

export interface MeiliIndexSummary {
  uid: string
  documents: number
  is_indexing: boolean
  primary_key: string | null
  /** What this index IS to the service, per the settings rows. */
  role: 'job' | 'places' | 'skills' | 'other'
}

export interface RecommenderIndexCatalog {
  /** False ⇒ Meilisearch is unreachable; `indexes` is empty for that reason. */
  reachable: boolean
  configured: { job_index: string; places_index: string; skills_index: string }
  indexes: MeiliIndexSummary[]
  /**
   * A configured name with no index behind it. Meilisearch answers a search
   * against a name that never existed with zero hits and NO error, so a typo in
   * a settings row is otherwise indistinguishable from a board with no jobs.
   */
  missing: string[]
}

/** `GET /admin/recommender/indexes` — every index on the engine. */
export function getRecommenderIndexes(
  options?: RequestOptions,
): Promise<RecommenderIndexCatalog> {
  return c.get<RecommenderIndexCatalog>('/admin/recommender/indexes', options)
}

export interface MeiliBrowseResult {
  index: string
  /** `list` pages stably in primary-key order; `search` pages by relevance. */
  mode: 'list' | 'search'
  /** `_vectors` is replaced by `_vector_dims` (embedder -> width). */
  documents: Array<Record<string, unknown>>
  estimated_total: number | null
  processing_time_ms: number | null
  /** Embedders the INDEX declares, or null when it declares none. */
  embedders: Record<string, number | null> | null
}

/**
 * `GET /admin/recommender/indexes/documents` — list or search raw engine data.
 *
 * An empty `q` LISTS in primary-key order; a non-empty `q` SEARCHES by
 * relevance. `filter` takes a Meilisearch expression (`kind = city`); an invalid
 * one yields an empty result rather than an error, because the operator is
 * usually still typing it.
 */
export function browseIndexDocuments(
  params: { index: string; q?: string; filter?: string; limit?: number; offset?: number },
  options?: RequestOptions,
): Promise<MeiliBrowseResult> {
  const query = new URLSearchParams({ index: params.index })
  if (params.q?.trim()) query.set('q', params.q.trim())
  if (params.filter?.trim()) query.set('filter', params.filter.trim())
  if (params.limit !== undefined) query.set('limit', String(params.limit))
  if (params.offset !== undefined) query.set('offset', String(params.offset))
  return c.get<MeiliBrowseResult>(`/admin/recommender/indexes/documents?${query.toString()}`, options)
}

export interface PlacesStatus {
  index_name: string
  reachable: boolean
  documents: number
  /** Split by kind: an index with localities and no cities is HALF loaded. */
  cities: number
  localities: number
  /** Whether this server can restore without the GeoNames/OSM build pipeline. */
  snapshot_available: boolean
  snapshot_documents: number | null
}

/** `GET /admin/recommender/places` — is the gazetteer loaded? */
export function getPlacesStatus(options?: RequestOptions): Promise<PlacesStatus> {
  return c.get<PlacesStatus>('/admin/recommender/places', options)
}

export interface LoadPlacesResult {
  index_name: string
  loaded: number
  batches: number
  cleared: boolean
  duration_ms: number
  /** The count AFTER the load — an upsert leaves it unchanged, so trust this. */
  documents: number
  source: 'snapshot' | 'csv'
}

/**
 * `POST /admin/recommender/places/load` — restore the gazetteer on demand.
 *
 * Prefers the snapshot bundled with the deployment, falling back to the CSVs.
 * Idempotent (documents are keyed on the gazetteer id); `clear` empties first,
 * which is for a genuine rebuild rather than a routine reload.
 */
export function loadPlaces(
  clear = false,
  options?: RequestOptions,
): Promise<LoadPlacesResult> {
  return c.post<LoadPlacesResult>('/admin/recommender/places/load', { clear }, options)
}

/** Skills-search index (direction change, 2026-08-18) — the typo-tolerant
 * projection the manual skill-adding picker searches, not extraction. */
export interface SkillsIndexStatus {
  index_name: string
  reachable: boolean
  documents: number
  /** A healthy load has `documents === active_skills`. */
  active_skills: number
}

/** `GET /admin/recommender/skills` — is the skills-search index loaded? */
export function getSkillsIndexStatus(options?: RequestOptions): Promise<SkillsIndexStatus> {
  return c.get<SkillsIndexStatus>('/admin/recommender/skills', options)
}

export interface LoadSkillsIndexResult {
  index_name: string
  loaded: number
  batches: number
  cleared: boolean
  /** The count AFTER the load — an upsert leaves it unchanged, so trust this. */
  documents: number
}

/**
 * `POST /admin/recommender/skills/load` — (re)populate the skills-search
 * index from every ACTIVE skill-master row. Always live from Postgres —
 * unlike the gazetteer there is no snapshot/CSV concept. Idempotent
 * (documents keyed on `skill_id`); `clear` empties first, for a genuine
 * rebuild rather than a routine reload.
 */
export function loadSkillsIndex(
  clear = false,
  options?: RequestOptions,
): Promise<LoadSkillsIndexResult> {
  return c.post<LoadSkillsIndexResult>('/admin/recommender/skills/load', { clear }, options)
}

export interface LoadSynonymsResult {
  rows: number
  terms: number
  indexes: string[]
  duration_ms: number
}

/**
 * `POST /admin/recommender/synonyms/load` — apply the BFSI vocabulary to the
 * job AND places indexes. A whole-map REPLACE, not a merge.
 */
export function loadSearchSynonyms(options?: RequestOptions): Promise<LoadSynonymsResult> {
  return c.post<LoadSynonymsResult>('/admin/recommender/synonyms/load', {}, options)
}

// ── Bulk-import support: templates + dry-run preview ───────────────────────────

/**
 * ONE problem with ONE row of an uploaded file, said so it can be acted on:
 * which column, what is in it, why that is wrong, and what to do instead.
 *
 * `message` is the single-line spelling the POST-upload report has always
 * carried — kept so the older failures table and the new preview table are
 * describing the same judgement rather than two similar ones.
 */
export interface ImportIssue {
  /** The offending column, spelled as the template spells it. Null = the whole row. */
  column: string | null
  /** The offending cell. Null when the column carries PII, or when no one cell is to blame. */
  value: string | null
  reason: string
  fix: string
  message: string
}

/** What committing the file would DO with one row. */
export type ImportRowAction = 'CREATE' | 'UPDATE' | 'DEACTIVATE' | 'NO_CHANGE' | 'ERROR'

export interface ImportRowPreview {
  /** 1-based DATA row — the number the spreadsheet shows minus the header. */
  row: number
  action: ImportRowAction
  summary: string
  issues: ImportIssue[]
  /** The row lands, but something in it was dropped or defaulted. */
  warnings: ImportIssue[]
  /** The row's cells for display. PII columns arrive already redacted. */
  cells: Record<string, string>
}

export interface ImportPreviewReport {
  kind: string
  /** Which accepted template the backend recognised the file as. */
  format: string
  file_name: string | null
  total: number
  valid: number
  invalid: number
  warnings: number
  columns: string[]
  rows: ImportRowPreview[]
}

export interface ImportColumnSpec {
  name: string
  required: boolean
  description: string
  example: string
  fix: string
}

export interface ImportFormatSpec {
  key: string
  label: string
  sheet_name: string
  columns: ImportColumnSpec[]
}

export interface ImportKindSpec {
  key: string
  label: string
  description: string
  roles: string[]
  formats: ImportFormatSpec[]
  notes: string[]
  /** Server path of the primary format's CSV template. */
  template_url: string
}

/** The import kinds this deployment exposes, with their column contracts. */
export function listImportKinds(options?: RequestOptions): Promise<ImportKindSpec[]> {
  return c.get<ImportKindSpec[]>('/imports/templates', options)
}

/**
 * DRY-RUN an import file: the backend runs the same validation the commit route
 * runs, writes nothing, and returns a verdict per row.
 *
 * A structurally unusable file (empty, ragged, a missing REQUIRED column, the
 * wrong sheet) still throws — that is a FILE problem no per-row report can
 * explain, and it is the same refusal the commit would give.
 */
export function previewImport(
  kind: string,
  file: File,
  options?: RequestOptions,
): Promise<ImportPreviewReport> {
  const form = new FormData()
  form.append('file', file)
  return c.postMultipart<ImportPreviewReport>(`/imports/${kind}/preview`, form, options)
}

/**
 * Download a kind's CSV template and hand it to the browser as a file.
 *
 * Fetched through the API client rather than linked directly, because the route
 * is bearer-authenticated and a bare `<a href>` carries no Authorization
 * header — it would download the login page's 401 envelope as a .csv.
 */
export async function downloadImportTemplate(
  kind: string,
  fileName?: string,
  options?: RequestOptions,
): Promise<void> {
  const blob = await c.getBlob(`/imports/${kind}/template.csv`, options)
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName ?? `${kind}-template.csv`
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}
